import React, { useState, useEffect, useContext } from "react";
import {
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  FileText,
  Search,
  RefreshCw,
  Sun,
  Heart,
  Home,
  Send,
  Loader,
  ChevronRight,
  X,
  Settings,
  BarChart3,
  PlusCircle,
  Trash2,
  Users,
} from "lucide-react";
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";
import ReusableDropdown from "../components/ReusableDropdown";
import AlertModal from "../components/AlertModal";
import { apiFetch } from "../context/AuthContext"; // ✅ Import apiFetch
import { useProfile } from "../hooks/useProfile";

// ===============================================
const translations = {
  en: {
    leaveManagement: "Leave Management",
    reviewAndApprove: "Review and approve employee leave requests",
    errorLoadingRequests: "Error Loading Requests",
    pendingRequests: "Pending Requests",
    approved: "Approved",
    rejected: "Rejected",
    totalRequests: "Total Requests",
    pending: "Pending",
    searchAndFilter: "Search & Filter",
    findAndFilter: "Find and filter leave requests",
    showing: "Showing",
    of: "of",
    requests: "requests",
    searchEmployee: "Search Employee",
    searchPlaceholder: "Search employee or reason...",
    leaveType: "Leave Type",
    allTypes: "All Types",
    annual: "Annual",
    sick: "Sick",
    maternity: "Maternity",
    emergency: "Emergency",
    wfh: "Work From Home",
    partialDay: "Partial Day Request",
    actions: "Actions",
    refresh: "Refresh",
    loading: "Loading...",
    leaveRequests: "Leave Requests",
    loadingRequests: "Loading requests...",
    noRequestsFound: "No leave requests found",
    tryAdjusting: "Try adjusting your filters or search criteria",
    leaveRequestReview: "Leave Request Review",
    employeeInformation: "Employee Information",
    requestedOn: "Requested on",
    requestDetails: "Request Details",
    startDate: "Start Date",
    endDate: "End Date",
    duration: "Duration",
    days: "days",
    status: "Status",
    reasonForLeave: "Reason for Leave",
    yourDecision: "Your Decision",
    approve: "Approve",
    reject: "Reject",
    approvalComments: "Approval Comments",
    reasonForRejection: "Reason for Rejection",
    optional: "(Optional)",
    approvalCommentsPlaceholder: "Add any notes about this approval...",
    rejectionReasonPlaceholder: "Please explain why this request is being rejected...",
    cancel: "Cancel",
    processing: "Processing...",
    submitApproval: "Submit Approval",
    submitRejection: "Submit Rejection",
    submitDecision: "Submit Decision",
    unknownEmployee: "Unknown Employee",
    leave: "Leave",
    all: "All",
    manageEmployeeLeaves: "Manage Employee Leaves",
    manageEmployeeLeavesDesc: "Select an employee to view and manage their manual leaves",
    selectEmployee: "Select Employee",
    searchEmployeePlaceholder: "Search by name or code...",
    noEmployeesFound: "No employees found",
    employeeLeaves: "Employee Leaves",
    noLeavesFound: "No manual leaves found for this employee",
    addNewLeave: "Add New Leave",
    createLeave: "Create Leave",
    creating: "Creating...",
    deleting: "Deleting...",
    confirmDelete: "Delete this leave?",
    back: "Back",
    manualLeaves: "Manual Leaves",
    leaveCreatedSuccess: "Leave created successfully",
    leaveDeletedSuccess: "Leave deleted successfully",
    selectLeaveType: "Select leave type",
    custom: "Custom",
    customTypePlaceholder: "Enter leave type name...",
    halfDayType: "Half-Day Type",
    firstHalf: "First Half (Morning)",
    secondHalf: "Second Half (Afternoon)",
    selectHalfDayType: "Select half-day type",
    balanceRemaining: "Remaining Balance",
    balanceDays: "days remaining",
    insufficientBalance: "Insufficient Balance",
    insufficientBalanceMsg: "Employee does not have enough balance. Remaining",
    cannotDeletePastLeave: "Cannot Delete",
    cannotDeletePastLeaveMsg: "Leaves cannot be deleted more than 2 days after the start date has passed.",
    quotaLabel: "/ total",
    holidayManagement: "Holiday Management",
    holidayManagementDesc: "Manage official holidays per client and company",
    holidayName: "Holiday Name",
    holidayDate: "Holiday Date",
    clientNameLabel: "Client Name",
    workingCompanyLabel: "Working Company",
    createHoliday: "Create Holiday",
    saveChanges: "Save Changes",
    editHoliday: "Edit Holiday",
    deleteHoliday: "Delete Holiday",
    confirmDeleteHoliday: "Are you sure you want to delete this holiday?",
    holidayCreated: "Holiday created successfully",
    holidayUpdated: "Holiday updated successfully",
    holidayDeleted: "Holiday deleted successfully",
    noHolidaysFound: "No holidays found",
    filterByClient: "Filter by Client",
    filterByCompany: "Filter by Company",
    allClients: "All Clients",
    allCompanies: "All Companies",
    holidayNamePlaceholder: "e.g. Eid Al-Fitr",
    selectClient: "Select Client",
    selectCompany: "Select Company",
    selectWorkingCompany: "Select Working Company",
    selectCompanyForHoliday: "Choose a company to create/edit holiday",
    chooseCompanyFromList: "Choose a company from the list below",
    loadingCompanies: "Loading companies...",
    noCompaniesFound: "No companies found",
    date: "Date",
  },
  ar: {
    leaveManagement: "إدارة الإجازات",
    reviewAndApprove: "مراجعة والموافقة على طلبات إجازات الموظفين",
    errorLoadingRequests: "خطأ في تحميل الطلبات",
    pendingRequests: "الطلبات المعلقة",
    approved: "موافق عليها",
    rejected: "مرفوضة",
    totalRequests: "إجمالي الطلبات",
    pending: "معلق",
    searchAndFilter: "البحث والفلترة",
    findAndFilter: "ابحث وفلتر طلبات الإجازات",
    showing: "عرض",
    of: "من",
    requests: "طلب",
    searchEmployee: "البحث عن موظف",
    searchPlaceholder: "ابحث عن موظف أو سبب...",
    leaveType: "نوع الإجازة",
    allTypes: "جميع الأنواع",
    annual: "سنوية",
    sick: "مرضية",
    maternity: "أمومة",
    emergency: "طارئة",
    wfh: "العمل من المنزل",
    partialDay: "طلب يوم جزئي",
    actions: "الإجراءات",
    refresh: "تحديث",
    loading: "جاري التحميل...",
    leaveRequests: "طلبات الإجازات",
    loadingRequests: "جاري تحميل الطلبات...",
    noRequestsFound: "لم يتم العثور على طلبات إجازة",
    tryAdjusting: "حاول تعديل الفلاتر أو معايير البحث",
    leaveRequestReview: "مراجعة طلب الإجازة",
    employeeInformation: "معلومات الموظف",
    requestedOn: "تم الطلب في",
    requestDetails: "تفاصيل الطلب",
    startDate: "تاريخ البداية",
    endDate: "تاريخ النهاية",
    duration: "المدة",
    days: "أيام",
    status: "الحالة",
    reasonForLeave: "سبب الإجازة",
    yourDecision: "قرارك",
    approve: "موافقة",
    reject: "رفض",
    approvalComments: "تعليقات الموافقة",
    reasonForRejection: "سبب الرفض",
    optional: "(اختياري)",
    approvalCommentsPlaceholder: "أضف أي ملاحظات حول هذه الموافقة...",
    rejectionReasonPlaceholder: "يرجى شرح سبب رفض هذا الطلب...",
    cancel: "إلغاء",
    processing: "جاري المعالجة...",
    submitApproval: "إرسال الموافقة",
    submitRejection: "إرسال الرفض",
    submitDecision: "إرسال القرار",
    unknownEmployee: "موظف غير معروف",
    leave: "إجازة",
    all: "الكل",
    manageEmployeeLeaves: "إدارة إجازات الموظفين",
    manageEmployeeLeavesDesc: "اختر موظفاً لعرض وإدارة إجازاته اليدوية",
    selectEmployee: "اختر موظفاً",
    searchEmployeePlaceholder: "ابحث بالاسم أو الكود...",
    noEmployeesFound: "لا يوجد موظفون",
    employeeLeaves: "إجازات الموظف",
    noLeavesFound: "لا توجد إجازات يدوية لهذا الموظف",
    addNewLeave: "إضافة إجازة جديدة",
    createLeave: "إنشاء إجازة",
    creating: "جاري الإنشاء...",
    deleting: "جاري الحذف...",
    confirmDelete: "حذف هذه الإجازة؟",
    back: "رجوع",
    manualLeaves: "الإجازات اليدوية",
    leaveCreatedSuccess: "تم إنشاء الإجازة بنجاح",
    leaveDeletedSuccess: "تم حذف الإجازة بنجاح",
    selectLeaveType: "اختر نوع الإجازة",
    custom: "مخصص",
    customTypePlaceholder: "أدخل اسم نوع الإجازة...",
    halfDayType: "نوع نصف اليوم",
    firstHalf: "النصف الأول (صباحي)",
    secondHalf: "النصف الثاني (عصري)",
    selectHalfDayType: "اختر نوع نصف اليوم",
    balanceRemaining: "الرصيد المتبقي",
    balanceDays: "يوم متبقي",
    insufficientBalance: "رصيد غير كافٍ",
    insufficientBalanceMsg: "ليس لدى الموظف رصيد كافٍ. المتبقي",
    cannotDeletePastLeave: "لا يمكن الحذف",
    cannotDeletePastLeaveMsg: "لا يمكن حذف الإجازات بعد مرور أكثر من يومين من تاريخ البداية.",
    quotaLabel: "/ الإجمالي",
    holidayManagement: "إدارة الإجازات الرسمية",
    holidayManagementDesc: "إدارة الإجازات الرسمية لكل عميل وشركة",
    holidayName: "اسم الإجازة",
    holidayDate: "تاريخ الإجازة",
    clientNameLabel: "اسم العميل",
    workingCompanyLabel: "الشركة",
    createHoliday: "إنشاء إجازة",
    saveChanges: "حفظ التغييرات",
    editHoliday: "تعديل الإجازة",
    deleteHoliday: "حذف الإجازة",
    confirmDeleteHoliday: "هل أنت متأكد من حذف هذه الإجازة؟",
    holidayCreated: "تم إنشاء الإجازة بنجاح",
    holidayUpdated: "تم تحديث الإجازة بنجاح",
    holidayDeleted: "تم حذف الإجازة بنجاح",
    noHolidaysFound: "لا توجد إجازات رسمية",
    filterByClient: "فلتر بالعميل",
    filterByCompany: "فلتر بالشركة",
    allClients: "جميع العملاء",
    allCompanies: "جميع الشركات",
    holidayNamePlaceholder: "مثال: عيد الفطر",
    selectClient: "اختر العميل",
    selectCompany: "اختر الشركة",
    selectWorkingCompany: "اختر الشركة العاملة",
    selectCompanyForHoliday: "اختر شركة لإنشاء/تعديل الإجازة",
    chooseCompanyFromList: "اختر شركة من القائمة أدناه",
    loadingCompanies: "جاري تحميل الشركات...",
    noCompaniesFound: "لم يتم العثور على شركات",
    date: "التاريخ",
  }
};

const LeaveApprovalPage = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();
  const t = translations[language];
  const isRTL = language === "ar";
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // State Management
  const [refreshInterval, setRefreshInterval] = useState(54000000); // 15 ساعة بالـ milliseconds
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [wfhRequests, setWfhRequests] = useState([]);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("pending");
  const [filterType, setFilterType] = useState("all");
  const [decision, setDecision] = useState("");
  const [comments, setComments] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState({ open: false, title: "", message: "", variant: "info" });

  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    total: 0,
  });
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [userRole, setUserRole] = useState("");

  // ── Manage Employee Leaves modal state ──
  const [showManageLeaveModal, setShowManageLeaveModal] = useState(false);
  const [empModalStep, setEmpModalStep] = useState("select"); // "select" | "manage"
  const [empModalEmployees, setEmpModalEmployees] = useState([]);
  const [empModalLoading, setEmpModalLoading] = useState(false);
  const [empModalSearch, setEmpModalSearch] = useState("");
  const [selectedEmpForLeave, setSelectedEmpForLeave] = useState(null);
  const [empLeaves, setEmpLeaves] = useState([]);
  const [empLeavesLoading, setEmpLeavesLoading] = useState(false);
  const [deletingLeaveIds, setDeletingLeaveIds] = useState(new Set());
  const [newLeaveForm, setNewLeaveForm] = useState({ leaveType: "", customLeaveType: "", startDate: "", endDate: "", halfDayType: "" });
  const [createLeaveLoading, setCreateLeaveLoading] = useState(false);
  const [empQuota, setEmpQuota] = useState(null);
  const [empQuotaLoading, setEmpQuotaLoading] = useState(false);

  // ── Holiday Management state ──────────────────────────────────────────────
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidayList, setHolidayList] = useState([]);
  const [holidayLoading, setHolidayLoading] = useState(false);
  const [holidayClientsList, setHolidayClientsList] = useState([]);
  const [holidayCompanies, setHolidayCompanies] = useState([]);
  const [holidayForm, setHolidayForm] = useState({ name: "", date: "", clientName: "", workingCompany: "" });
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidaySubmitting, setHolidaySubmitting] = useState(false);
  const [holidayDeleteConfirmId, setHolidayDeleteConfirmId] = useState(null);
  const [holidayClientFilter, setHolidayClientFilter] = useState("");
  const [holidayCompanyFilter, setHolidayCompanyFilter] = useState("");
  const [holidaySearch, setHolidaySearch] = useState("");
  const getCurrentViewType = () => {
    const storedManagerViewType = localStorage.getItem("managerViewType");
    const storedViewType = localStorage.getItem("viewType");
    return String(storedManagerViewType || storedViewType || "admin").toLowerCase();
  };

  const getLeaveActionPermissions = (leaveRecord) => {
    const currentViewType = getCurrentViewType();
    const isManagerInManagerView = userRole === "manager" && currentViewType === "manager";
    const isPrivilegedRole = userRole === "admin" || userRole === "superadmin";

    let leaveFeatures = [];
    try {
      const adminLevel = profile?.adminLevel || null;
      leaveFeatures = adminLevel?.features?.leaves || [];
    } catch (parseError) {
      leaveFeatures = [];
    }

    const hasApprovePermission = isPrivilegedRole || isManagerInManagerView || leaveFeatures.includes("approve");
    const hasRejectPermission = isPrivilegedRole || isManagerInManagerView || leaveFeatures.includes("reject");

    return {
      hasApprovePermission,
      hasRejectPermission,
      hasAnyPermission: hasApprovePermission || hasRejectPermission,
      currentViewType,
      isManagerInManagerView,
      leaveRecord,
    };
  };
  const clientName = profile?.clientName || localStorage.getItem("clientName");
  const normalizeStatusValue = (value) => {
    if (typeof value !== "string") return "";
    return value.trim().toLowerCase();
  };
  const normalizeLeaveTypeValue = (value) => {
    if (typeof value !== "string") return "";
    return value.trim().toLowerCase();
  };
  const normalizeInfinityCodeValue = (value) => {
    if (value === undefined || value === null) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;
    const direct = Number(trimmed);
    if (Number.isFinite(direct)) return direct;
    const digitsOnly = trimmed.replace(/[^0-9-]+/g, "");
    if (!digitsOnly) return null;
    const fallback = Number(digitsOnly);
    return Number.isFinite(fallback) ? fallback : null;
  };
  // Helper function to get authorization headers (token stays in localStorage)
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  };

  useEffect(() => {
    const role = profile?.role || localStorage.getItem("role");
    if (role) {
      setUserRole(role.toLowerCase());
    }
  }, [profile]);


  // Responsive styles
  const styles = {
    container: {
      marginTop: -10,
      direction: isRTL ? "rtl" : "ltr",
    },
    statCard: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
      padding: "1.55rem",
      marginTop: -40,
      marginLeft: isRTL ? 0 : 20,
      marginRight: isRTL ? 20 : 0,

      borderRadius: "1rem",
      transition: "all 0.3s ease",
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
          : "0 0.5rem 2rem rgba(0,0,0,0.1)",
      backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.05)",
      marginBottom: 70,
      cursor: "pointer",
      direction: isRTL ? "rtl" : "ltr",
    },
    welcomeTitle: {
      fontSize: "clamp(20px, 4vw, 28px)",
      fontWeight: "700",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      margin: "0 0 8px 0",
      textShadow: theme === "dark" ? "0 2px 4px rgba(0,0,0,0.3)" : "0 2px 4px rgba(0,0,0,0.1)",
      lineHeight: "1.2",
      textAlign: isRTL ? "right" : "left",
    },
    welcomeSubtitle: {
      fontSize: "clamp(14px, 2vw, 16px)",
      color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(26,26,26,0.7)",
      lineHeight: "1.4",
      textAlign: isRTL ? "right" : "left",
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))",
      gap: "clamp(16px, 3vw, 24px)",
      marginBottom: "5",

    },
    statBox: {
      textAlign: "center",
      padding: "clamp(16px, 3vw, 24px)",
      background: theme === "light" ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.05)",
      borderRadius: "12px",
      border: theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
    },
    controlsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(250px, 100%), 1fr))",
      gap: "clamp(16px, 3vw, 24px)",
      alignItems: "end",
      marginBottom: "5",
      position: "relative",
      zIndex: 1,
    },
    controlGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "clamp(8px, 1.5vw, 12px)",
      flex: 1,
      minWidth: 0,

    },
    label: {
      color: theme === "dark" ? "rgba(255,255,255,0.9)" : "#374151",
      fontSize: "clamp(12px, 1.5vw, 14px)",
      fontWeight: "700",
      letterSpacing: "0.3px",
      textTransform: "uppercase",
      display: "flex",
      alignItems: "center",
      gap: "clamp(4px, 1vw, 8px)",
      flexDirection: isRTL ? "row" : "row",
      textAlign: isRTL ? "right" : "left",


    },
    input: {
      background: theme === "light" ? "#f9fafb" : "rgba(255, 255, 255, 0.05)",
      border: theme === "light"
        ? "2px solid rgba(0, 0, 0, 0.2)"  // غير من 1px لـ 2px
        : "2px solid rgba(255, 255, 255, 0.2)",  // غير من 1px لـ 2px
      borderRadius: "clamp(10px, 1.5vw, 14px)",  // غير من 8px
      padding: "clamp(10px, 2vw, 14px) clamp(16px, 3vw, 20px)",  // زود الـ padding
      color: theme === "light" ? "#0f172a" : "#ffffff",
      fontSize: "clamp(12px, 1.5vw, 14px)",
      fontWeight: "600",  // ضيف weight
      outline: "none",

      transition: "all 0.3s ease",
      width: "100%",
      boxSizing: "border-box",
      textAlign: isRTL ? "right" : "left",
      direction: isRTL ? "rtl" : "ltr",
      backdropFilter: "blur(10px)",  // ضيف blur
      boxShadow: theme === "dark"
        ? "0 4px 20px rgba(0,0,0,0.2)"
        : "0 4px 20px rgba(0,0,0,0.05)",  // ضيف shadow

    },
    select: {
      background: theme === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.03)",
      border: theme === "dark"
        ? "2px solid rgba(255,255,255,0.15)"
        : "2px solid rgba(0,0,0,0.1)",
      borderRadius: "clamp(10px, 1.5vw, 14px)",
      padding: "clamp(10px, 2vw, 14px) clamp(16px, 3vw, 20px)",
      color: theme === "dark" ? "#ffffff" : "#0f172a",
      fontSize: "clamp(12px, 1.5vw, 14px)",
      fontWeight: "600",
      outline: "none",
      transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      width: "100%",
      cursor: "pointer",
      backdropFilter: "blur(10px)",
      boxShadow: theme === "dark"
        ? "0 4px 20px rgba(0,0,0,0.2)"
        : "0 4px 20px rgba(0,0,0,0.05)",
      boxSizing: "border-box",
      textAlign: isRTL ? "right" : "left",
      direction: isRTL ? "rtl" : "ltr",
    },
    inputWrapper: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      width: "100%",  // ضيف ده


    },
    secondaryButton: {
      background: theme === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.05)",
      color: theme === "dark" ? "#ffffff" : "#374151",
      border: theme === "dark"
        ? "2px solid rgba(255,255,255,0.15)"
        : "2px solid rgba(0,0,0,0.1)",
      borderRadius: "clamp(10px, 1.5vw, 14px)",
      padding: "clamp(10px, 2vw, 14px) clamp(16px, 3vw, 24px)",
      fontSize: "clamp(11px, 1.5vw, 14px)",
      fontWeight: "700",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "clamp(6px, 1vw, 10px)",
      transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      backdropFilter: "blur(10px)",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      minWidth: "clamp(100px, 15vw, 140px)",
      boxShadow: theme === "dark"
        ? "0 4px 20px rgba(0,0,0,0.2)"
        : "0 4px 20px rgba(0,0,0,0.05)",
      whiteSpace: "nowrap",
      flexDirection: isRTL ? "row" : "row",
    },
    primaryButton: {
      background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
      color: "white",
      border: "none",
      borderRadius: "clamp(10px, 1.5vw, 14px)",
      padding: "clamp(10px, 2vw, 14px) clamp(16px, 3vw, 24px)",
      fontSize: "clamp(11px, 1.5vw, 14px)",
      fontWeight: "700",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "clamp(6px, 1vw, 10px)",
      transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      //boxShadow: "0 8px 25px rgba(59, 130, 246, 0.3)",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      minWidth: "clamp(100px, 15vw, 140px)",
      justifyContent: "center",
      whiteSpace: "nowrap",
      flexDirection: isRTL ? "row" : "row",
    },
    requestItem: {
      background: theme === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(0, 0, 0, 0.02)",
      border: theme === "dark"
        ? "2px solid rgba(255, 255, 255, 0.08)"
        : "2px solid rgba(0, 0, 0, 0.05)",
      borderRadius: "16px",
      padding: "clamp(16px, 3vw, 20px)",
      marginBottom: "16px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      direction: isRTL ? "rtl" : "ltr",
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
    },
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      backdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      animation: "fadeIn 0.3s ease",
      padding: "clamp(10px, 2vw, 20px)",
      direction: isRTL ? "rtl" : "ltr",
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
    },
    modalContent: {
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      background: theme === "light"
        ? "rgba(255, 255, 255, 0.95)"
        : "rgba(30, 41, 59, 0.95)",
      padding: "clamp(20px, 4vw, 28px)",
      borderRadius: "clamp(12px, 2vw, 20px)",
      width: "min(90vw, 600px)",
      maxHeight: "85vh",
      overflowY: "auto",
      boxShadow: theme === "light"
        ? "0 10px 40px rgba(0, 0, 0, 0.15)"
        : "0 10px 40px rgba(0, 0, 0, 0.5)",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      border: theme === "dark"
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.05)",
      boxSizing: "border-box",
      direction: isRTL ? "rtl" : "ltr",
    },
    controlsHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "clamp(20px, 4vw, 32px)",
      paddingBottom: "clamp(12px, 2.5vw, 20px)",
      borderBottom: theme === "dark"
        ? "1px solid rgba(255,255,255,0.1)"
        : "1px solid rgba(0,0,0,0.1)",
      flexWrap: "wrap",
      gap: "16px",
      flexDirection: isRTL ? "row" : "row",
    },
    controlsTitle: {
      fontSize: "clamp(16px, 3vw, 20px)",
      fontWeight: "700",
      color: theme === "dark" ? "#ffffff" : "#0f172a",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "clamp(6px, 1vw, 10px)",
      letterSpacing: "0.3px",
      flexDirection: isRTL ? "row" : "row",
    },
    controlsSubtitle: {
      fontSize: "clamp(12px, 1.5vw, 14px)",
      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.6)",
      margin: "4px 0 0 0",
      textAlign: isRTL ? "right" : "left",
    },
    resultsInfo: {
      fontSize: "clamp(12px, 1.5vw, 14px)",
      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.6)",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontWeight: "500",
      flexDirection: isRTL ? "row" : "row",
    },
    tabsContainer: {
      display: "flex",
      marginTop: -50,
      gap: "clamp(10px, 2vw, 16px)",
      marginBottom: "5%",
      marginLeft: isRTL ? 0 : "clamp(12px, 3vw, 20px)",
      marginRight: isRTL ? "clamp(12px, 3vw, 20px)" : 0,
      padding: "clamp(12px, 2.5vw, 16px)",
      background: theme === "light"
        ? "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.98))"
        : "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(51, 65, 85, 0.6))",
      borderRadius: "clamp(14px, 2vw, 18px)",
      border: theme === "dark"
        ? "1.5px solid rgba(255,255,255,0.15)"
        : "1.5px solid rgba(0,0,0,0.08)",
      flexWrap: "wrap",
      flexDirection: isRTL ? "row" : "row",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      boxShadow: theme === "dark"
        ? "0 8px 32px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
        : "0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)",
      transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
    },
    tabButton: {
      padding: "clamp(10px, 2vw, 14px) clamp(18px, 3.5vw, 28px)",
      borderRadius: "clamp(10px, 1.8vw, 14px)",
      border: "1.5px solid transparent",
      background: "transparent",
      cursor: "pointer",
      fontWeight: "700",
      fontSize: "clamp(13px, 1.8vw, 15px)",
      transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
      color: theme === "dark" ? "rgba(255,255,255,0.65)" : "rgba(26,26,26,0.65)",
      letterSpacing: "0.4px",
      textTransform: "capitalize",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      position: "relative",
      overflow: "hidden",
      whiteSpace: "nowrap",
      flexDirection: isRTL ? "row" : "row",
    },
    activeTabButton: {
      background: "linear-gradient(135deg, #2e7cba 0%, #2e5ba8 100%)",
      color: "white",
      border: "1.5px solid rgba(46, 124, 186, 0.4)",
      boxShadow: theme === "dark"
        ? "0 8px 20px rgba(46, 124, 186, 0.35), inset 0 1px 0 rgba(255,255,255,0.2)"
        : "0 8px 20px rgba(46, 124, 186, 0.3), inset 0 1px 0 rgba(255,255,255,0.4)",
      fontWeight: "800",
      letterSpacing: "0.5px",
      position: "relative",
    },
    textarea: {
      width: "100%",
      minHeight: "120px",
      background: theme === "light" ? "#f9fafb" : "rgba(255, 255, 255, 0.05)",
      border: theme === "light"
        ? "1px solid rgba(0, 0, 0, 0.2)"
        : "1px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "12px",
      padding: "12px",
      fontSize: "14px",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      outline: "none",
      resize: "vertical",
      fontFamily: "inherit",
      textAlign: isRTL ? "right" : "left",
      direction: isRTL ? "rtl" : "ltr",
    },
    errorAlert: {
      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(220, 38, 38, 0.08))',
      border: '2px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '16px',
      padding: '20px',
      color: '#ef4444',
      fontSize: '15px',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      marginBottom: '20px',
      flexDirection: isRTL ? "row" : "row",
    }
  };

  // ── Manage Employee Leaves: fetch employees via visibility endpoint ──
  const fetchEmployeesForModal = async () => {
    setEmpModalLoading(true);
    try {
      const storedViewType = (localStorage.getItem("viewType") || "admin").toLowerCase();
      const managerCode = profile?.infinityCode || localStorage.getItem("infinityCode");
      const usp = new URLSearchParams();
      if (clientName) usp.set("clientName", clientName);
      if (storedViewType === "manager" && managerCode) usp.set("managerInfinityCode", managerCode);
      const url = `${BASE_URL}/employees/visibility?${usp.toString()}`;
      const resp = await apiFetch(url, {
        method: "GET",
        headers: { "Accept-Language": language, "x-view-type": storedViewType },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      const list = json.employees || json.data || json;
      setEmpModalEmployees(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to fetch employees for modal:", err);
      setEmpModalEmployees([]);
    } finally {
      setEmpModalLoading(false);
    }
  };

  const fetchEmployeeLeaves = async (infinityCode) => {
    setEmpLeavesLoading(true);
    try {
      const url = `${BASE_URL}/api/leaves?infinityCode=${encodeURIComponent(infinityCode)}&limit=100`;
      const resp = await apiFetch(url, { method: "GET", headers: { "Content-Type": "application/json" } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      const leaves = result.data?.leaves || result.leaves || [];
      setEmpLeaves(leaves);
    } catch (err) {
      console.error("Failed to fetch employee leaves:", err);
      setEmpLeaves([]);
    } finally {
      setEmpLeavesLoading(false);
    }
  };

  const handleDeleteEmpLeave = async (leaveId) => {
    // Use String() comparison to avoid type mismatch (number vs string IDs from API)
    const leave = empLeaves.find((l) => String(l.id) === String(leaveId));
    if (!leave) {
      setAlert({ open: true, title: language === "ar" ? "خطأ" : "Error", message: language === "ar" ? "لم يتم العثور على الإجازة" : "Leave not found", variant: "error" });
      return;
    }
    // Admins can delete at any time; non-admins are blocked after 2 days
    const currentRole = (localStorage.getItem("viewType") || "").toLowerCase();
    const isAdmin = currentRole === "admin" || currentRole === "superadmin" ||
      profile?.role === "admin" || profile?.role === "superadmin";
    if (!isAdmin) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = new Date(leave.startDate);
      startDate.setHours(0, 0, 0, 0);
      const cutoff = new Date(startDate);
      cutoff.setDate(cutoff.getDate() + 2);
      if (today > cutoff) {
        setAlert({ open: true, title: t.cannotDeletePastLeave, message: t.cannotDeletePastLeaveMsg, variant: "error" });
        return;
      }
    }
    setDeletingLeaveIds((prev) => new Set(prev).add(leaveId));
    try {
      const resp = await apiFetch(`${BASE_URL}/api/leaves/${leaveId}`, { method: "DELETE", headers: { "Content-Type": "application/json" } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setEmpLeaves((prev) => prev.filter((l) => l.id !== leaveId));
      // Refresh quota after deletion so balance display is up-to-date
      if (selectedEmpForLeave?.infinityCode) fetchEmpQuota(selectedEmpForLeave.infinityCode);
      setAlert({ open: true, title: language === "ar" ? "نجاح" : "Success", message: t.leaveDeletedSuccess, variant: "success" });
    } catch (err) {
      setAlert({ open: true, title: language === "ar" ? "خطأ" : "Error", message: err.message, variant: "error" });
    } finally {
      setDeletingLeaveIds((prev) => { const s = new Set(prev); s.delete(leaveId); return s; });
    }
  };

  const fetchEmpQuota = async (infinityCode) => {
    setEmpQuotaLoading(true);
    try {
      const year = new Date().getFullYear();
      const encoded = encodeURIComponent(infinityCode);
      const resp = await apiFetch(`${BASE_URL}/api/leaves/${encoded}/quota?year=${year}`, { method: "GET" });
      if (!resp.ok) throw new Error();
      const json = await resp.json();
      setEmpQuota(json?.data || json?.quota || json || null);
    } catch {
      setEmpQuota(null);
    } finally {
      setEmpQuotaLoading(false);
    }
  };

  const getLeaveTypeBalance = (leaveType) => {
    if (!empQuota) return null;
    const map = {
      annual: { quota: "annualQuota", used: "annualUsed" },
      sick: { quota: "sickQuota", used: "sickUsed" },
      maternity: { quota: "maternityQuota", used: "maternityUsed" },
      emergency: { quota: "emergencyQuota", used: "emergencyUsed" },
    };
    const keys = map[leaveType];
    if (!keys) return null;
    const total = Number(empQuota[keys.quota]) || 0;
    const used = Number(empQuota[keys.used]) || 0;
    return { total, used, remaining: Math.max(0, total - used) };
  };

  const handleCreateEmpLeave = async () => {
    const resolvedLeaveType = newLeaveForm.leaveType === "custom"
      ? newLeaveForm.customLeaveType.trim()
      : newLeaveForm.leaveType;

    // Validation: partial day uses single date + halfDayType
    if (!newLeaveForm.leaveType) {
      setAlert({ open: true, title: language === "ar" ? "تنبيه" : "Alert", message: language === "ar" ? "يرجى تعبئة جميع الحقول" : "Please fill all fields", variant: "error" });
      return;
    }
    if (newLeaveForm.leaveType === "partialDay") {
      if (!newLeaveForm.startDate || !newLeaveForm.halfDayType) {
        setAlert({ open: true, title: language === "ar" ? "تنبيه" : "Alert", message: language === "ar" ? "يرجى تعبئة جميع الحقول" : "Please fill all fields", variant: "error" });
        return;
      }
    } else {
      if (!newLeaveForm.startDate || !newLeaveForm.endDate) {
        setAlert({ open: true, title: language === "ar" ? "تنبيه" : "Alert", message: language === "ar" ? "يرجى تعبئة جميع الحقول" : "Please fill all fields", variant: "error" });
        return;
      }
    }

    if (newLeaveForm.leaveType === "custom" && !resolvedLeaveType) {
      setAlert({ open: true, title: language === "ar" ? "تنبيه" : "Alert", message: language === "ar" ? "يرجى كتابة نوع الإجازة المخصص" : "Please enter the custom leave type name", variant: "error" });
      return;
    }

    // Balance check for quota-tracked leave types
    const quotaTypes = ["annual", "sick", "maternity", "emergency"];
    if (quotaTypes.includes(resolvedLeaveType) || resolvedLeaveType === "annual") {
      const balance = getLeaveTypeBalance(resolvedLeaveType);
      if (balance !== null) {
        let days = 0;
        if (newLeaveForm.leaveType === "partialDay") {
          days = 0.5;
        } else {
          const start = new Date(newLeaveForm.startDate);
          const end = new Date(newLeaveForm.endDate);
          days = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24)) + 1;
        }
        if (days > balance.remaining) {
          setAlert({
            open: true,
            title: t.insufficientBalance,
            message: `${t.insufficientBalanceMsg}: ${balance.remaining} ${t.days}`,
            variant: "error",
          });
          return;
        }
      }
    }

    setCreateLeaveLoading(true);
    try {
      // Build payload - treat partial day differently
      const payload = {
        infinityCode: selectedEmpForLeave.infinityCode,
        leaveType: newLeaveForm.leaveType === "partialDay" ? "PARTIAL_DAY_LEAVE" : resolvedLeaveType,
        reason: language === "ar" ? "إضافة يدوية من الإدارة" : "Manual addition by admin",
        myAdmin: profile?.infinityCode || localStorage.getItem("infinityCode"),
        halfDayType: newLeaveForm.halfDayType,
      };

      if (newLeaveForm.leaveType === "partialDay") {
        payload.date = newLeaveForm.startDate;
        payload.startDate = newLeaveForm.startDate;
        payload.endDate = newLeaveForm.startDate;
        payload.halfDayType = newLeaveForm.halfDayType;
        payload.duration = 0.5;
      } else {
        payload.startDate = newLeaveForm.startDate;
        payload.endDate = newLeaveForm.endDate;
      }
      const resp = await apiFetch(`${BASE_URL}/api/leaves/admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error || errBody.message || `HTTP ${resp.status}`);
      }
      const result = await resp.json();
      const newLeave = result.data || result.leave || result;
      if (newLeave && newLeave.id) setEmpLeaves((prev) => [newLeave, ...prev]);
      setNewLeaveForm({ leaveType: "", customLeaveType: "", startDate: "", endDate: "" ,halfDayType: ""});
      // Refresh quota so balance display reflects the deduction
      if (selectedEmpForLeave?.infinityCode) fetchEmpQuota(selectedEmpForLeave.infinityCode);
      setAlert({ open: true, title: language === "ar" ? "نجاح" : "Success", message: t.leaveCreatedSuccess, variant: "success" });
      await fetchLeaveRequests(true);
    } catch (err) {
      setAlert({ open: true, title: language === "ar" ? "خطأ" : "Error", message: err.message, variant: "error" });
    } finally {
      setCreateLeaveLoading(false);
    }
  };

  const openManageLeaveModal = () => {
    setShowManageLeaveModal(true);
    setEmpModalStep("select");
    setSelectedEmpForLeave(null);
    setEmpLeaves([]);
    setEmpModalSearch("");
    setEmpQuota(null);
    setNewLeaveForm({ leaveType: "", customLeaveType: "", startDate: "", endDate: "", halfDayType: "" });
    fetchEmployeesForModal();
  };

  // ── Holiday Management handlers ──────────────────────────────────────────

  const fetchHolidays = async () => {
    setHolidayLoading(true);
    try {
      const params = new URLSearchParams();
      if (holidayClientFilter) params.set("clientName", holidayClientFilter);
      if (holidayCompanyFilter) params.set("workingCompany", holidayCompanyFilter);
      if (holidaySearch) params.set("search", holidaySearch);
      const resp = await apiFetch(`${BASE_URL}/api/leaves/manage/holidays?${params.toString()}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      setHolidayList(result.data || []);
    } catch (err) {
      console.error("fetchHolidays error:", err);
      setHolidayList([]);
    } finally {
      setHolidayLoading(false);
    }
  };

  const fetchClientsForHoliday = async () => {
    try {
      const resp = await apiFetch(`${BASE_URL}/api/employees/clients/all`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      console.log('📡 fetchClientsForHoliday -> response status:', resp.status);
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        console.error('❌ fetchClientsForHoliday error body:', errBody);
        throw new Error(`HTTP ${resp.status}`);
      }
      const result = await resp.json().catch(() => ({}));
      console.log('📡 fetchClientsForHoliday -> raw response JSON:', JSON.stringify(result));

      // Extract clients from response (try multiple formats)
      let clients = result.data || result.clients || result;
      if (clients && typeof clients === 'object' && !Array.isArray(clients)) {
        // If single object, wrap in array
        clients = [clients];
      }

      // Ensure array
      if (!Array.isArray(clients)) {
        console.warn('❌ clients is not an array:', clients);
        clients = [];
      }

      // Normalize: convert strings to objects with clientName property
      const normalized = clients
        .map((c) => {
          if (typeof c === 'string') {
            return { clientName: c, clientCode: '' };
          }
          // Ensure clientName exists and is not empty
          const name = (c.clientName || c.name || '').toString().trim();
          if (!name) return null; // Filter out empty names
          return { clientName: name, clientCode: c.clientCode || '' };
        })
        .filter((c) => c !== null); // Remove null entries

      console.log('✅ fetchClientsForHoliday -> filtered/normalized clients:', JSON.stringify(normalized));
      console.log('✅ fetchClientsForHoliday -> will set holidayClientsList with', normalized.length, 'items');
      setHolidayClientsList(normalized);
    } catch (err) {
      console.error("❌ fetchClientsForHoliday error:", err);
      setHolidayClientsList([]);
    }
  };

  const fetchCompaniesForHoliday = async (selectedClient) => {
    if (!selectedClient) { setHolidayCompanies([]); return; }
    try {
      const resp = await apiFetch(
        `${BASE_URL}/api/company-config/${encodeURIComponent(selectedClient)}/detect-companies`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();

      // Extract companies from response (handle multiple response formats)
      let companies = [];
      const safeCompanyName = (company) => {
        if (typeof company === "string") return company.trim();
        const raw = company?.companyName ?? company?.name ?? "";
        return (typeof raw === "string" ? raw : String(raw || "")).trim();
      };
      if (result.success && result.data && result.data.companies) {
        // Standard format from API: { success: true, data: { companies: [...] } }
        companies = result.data.companies
          .map((company) => ({ name: safeCompanyName(company), companyName: safeCompanyName(company) }))
          .filter((c) => c.name);
      } else if (result.data && Array.isArray(result.data)) {
        // Direct array format: { data: [...] }
        companies = result.data
          .map((company) => ({ name: safeCompanyName(company), companyName: safeCompanyName(company) }))
          .filter((c) => c.name);
      } else if (Array.isArray(result)) {
        // Direct array response
        companies = result
          .map((company) => ({ name: safeCompanyName(company), companyName: safeCompanyName(company) }))
          .filter((c) => c.name);
      }

      console.log("✅ Fetched companies for holiday:", companies);
      setHolidayCompanies(companies);
    } catch (err) {
      console.error("fetchCompaniesForHoliday error:", err);
      setHolidayCompanies([]);
    }
  };

  const openHolidayModal = () => {
    const adminClient = profile?.clientName || localStorage.getItem("clientName") || "";
    setHolidayForm({ name: "", date: "", clientName: adminClient, workingCompany: "" });
    setHolidayClientFilter("");
    setHolidayCompanyFilter("");
    setHolidaySearch("");
    setEditingHoliday(null);
    fetchCompaniesForHoliday(adminClient);
    fetchHolidays();
    fetchClientsForHoliday();
    setShowHolidayModal(true);
  };

  const handleHolidayFormChange = (field, value) => {
    setHolidayForm((prev) => ({ ...prev, [field]: value }));
    if (field === "clientName") {
      setHolidayForm((prev) => ({ ...prev, clientName: value, workingCompany: "" }));
      fetchCompaniesForHoliday(value);
    }
  };

  const handleStartEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    const dateStr = holiday.date ? new Date(holiday.date).toISOString().split("T")[0] : "";
    const adminClient = profile?.clientName || localStorage.getItem("clientName") || holiday.clientName || "";
    setHolidayForm({
      name: holiday.name || "",
      date: dateStr,
      clientName: adminClient,
      workingCompany: holiday.workingCompany || "",
    });
    setShowHolidayModal(true);
  };

  const handleCancelEditHoliday = () => {
    setEditingHoliday(null);
    setHolidayForm({ name: "", date: "", clientName: "", workingCompany: "" });
    setHolidayCompanies([]);
  };

  const handleSubmitHoliday = async () => {
    if (!holidayForm.name || !holidayForm.date || !holidayForm.clientName || !holidayForm.workingCompany) {
      setAlert({ open: true, title: language === "ar" ? "خطأ" : "Error", message: language === "ar" ? "يرجى ملء جميع الحقول المطلوبة" : "Please fill in all required fields", variant: "error" });
      return;
    }
    setHolidaySubmitting(true);
    try {
      let resp;
      const payload = {
        name: holidayForm.name,
        date: holidayForm.date,
        clientName: holidayForm.clientName,
        workingCompany: holidayForm.workingCompany,
      };
      console.log('📤 About to send holiday:', JSON.stringify(payload));

      if (editingHoliday) {
        console.log('✏️ Editing holiday ID:', editingHoliday.id);
        resp = await apiFetch(`${BASE_URL}/api/leaves/manage/holidays/${editingHoliday.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        console.log('✨ Creating new holiday');
        resp = await apiFetch(`${BASE_URL}/api/leaves/manage/holidays`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        console.error('❌ Holiday create/update failed', resp.status, errBody);
        throw new Error(errBody.error || `HTTP ${resp.status}`);
      }
      const result = await resp.json();
      console.log('✅ Holiday API response:', { status: resp.status, body: result });
      const saved = result.data;
      if (editingHoliday) {
        setHolidayList((prev) => prev.map((h) => (h.id === saved.id ? saved : h)));
        setAlert({ open: true, title: language === "ar" ? "نجاح" : "Success", message: t.holidayUpdated, variant: "success" });
        setEditingHoliday(null);
        // After editing, reset form and close modal
        setHolidayForm({ name: "", date: "", clientName: "", workingCompany: "" });
        setHolidayCompanies([]);
        setShowHolidayModal(false);
      } else {
        // After creating, add to list and KEEP MODAL OPEN
        setHolidayList((prev) => [saved, ...prev]);
        setAlert({ open: true, title: language === "ar" ? "نجاح" : "Success", message: t.holidayCreated, variant: "success" });
        // Keep clientName constant, clear only form fields for next holiday
        const currentClient = holidayForm.clientName;
        setHolidayForm({ name: "", date: "", clientName: currentClient, workingCompany: "" });
        // Refresh companies for this client
        fetchCompaniesForHoliday(currentClient);
      }
    } catch (err) {
      setAlert({ open: true, title: language === "ar" ? "خطأ" : "Error", message: err.message, variant: "error" });
    } finally {
      setHolidaySubmitting(false);
    }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      const resp = await apiFetch(`${BASE_URL}/api/leaves/manage/holidays/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${resp.status}`);
      }
      setHolidayList((prev) => prev.filter((h) => h.id !== id));
      setHolidayDeleteConfirmId(null);
      setAlert({ open: true, title: language === "ar" ? "نجاح" : "Success", message: t.holidayDeleted, variant: "success" });
    } catch (err) {
      setAlert({ open: true, title: language === "ar" ? "خطأ" : "Error", message: err.message, variant: "error" });
    }
  };

  // Refresh holiday list when filters change
  useEffect(() => {
    if (showHolidayModal) fetchHolidays(); // eslint-disable-line react-hooks/exhaustive-deps
  }, [holidayClientFilter, holidayCompanyFilter, holidaySearch, showHolidayModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch clients for holiday when modal opens
  useEffect(() => {
    if (showHolidayModal) {
      console.log('🎯 Holiday modal opened - fetching clients');
      fetchClientsForHoliday();
    }
  }, [showHolidayModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch leave requests from backend
  const fetchLeaveRequests = async (silentRefresh = false) => {
    if (!silentRefresh) {
      setIsLoading(true);
    } else {
      setIsAutoRefreshing(true);
    }

    setError(null);

    try {
      const storedInfinityCode = normalizeInfinityCodeValue(profile?.infinityCode ?? localStorage.getItem('infinityCode'));
      const storedViewType = (localStorage.getItem('viewType') || '').toLowerCase();
      let url = null;
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      const myAdminQuery = Number.isFinite(storedInfinityCode) ? `myAdmin=${storedInfinityCode}` : '';

      // If viewType is 'admin', use visibility endpoint to filter by admin level rules
      if (storedViewType === 'admin') {
        url = `${BASE_URL}/api/leaves/visibility${clientName ? `?clientName=${encodeURIComponent(clientName)}` : ''}`;
      } else if (clientName && clientName !== 'Infinity') {
        // Manager view or other views - use existing client endpoint
        url = `${BASE_URL}/api/leaves/client/${clientName}${myAdminQuery ? `?${myAdminQuery}` : ''}`;
      } else {
        url = `${BASE_URL}/api/leaves${myAdminQuery ? `?${myAdminQuery}` : ''}`;
      }

      // ✅ Use apiFetch instead of fetch
      const response = await apiFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data && result.data.leaves) {
        const leaves = result.data.leaves.map((leave) => ({
          ...leave,
          status: normalizeStatusValue(leave.status || leave.currentStatus || leave.State || leave.Status),
          leaveType: normalizeLeaveTypeValue(leave.leaveType || leave.type || leave.leave_type || leave.LeaveType),
        }));

        // ✅ Check if there are changes
        const hasChanges = JSON.stringify(leaves) !== JSON.stringify(leaveRequests);

        if (hasChanges && silentRefresh) {
          console.log('✨ New updates detected!');
        }

        setLeaveRequests(leaves);
        calculateStats(leaves);
        applyFilters(leaves);
        setLastRefresh(new Date());

        if (!silentRefresh) {
          console.log(`✅ Successfully loaded ${leaves.length} leave requests`);
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error("Error fetching leave requests:", error);
      if (!silentRefresh) {
        setError(error.message);
        setLeaveRequests([]);
        setFilteredRequests([]);
      }
    } finally {
      setIsLoading(false);
      setIsAutoRefreshing(false);
    }
  };

  // // ✅ دالة لإظهار Notification
  // const showUpdateNotification = () => {
  //   const notification = document.createElement('div');
  //   notification.style.cssText = `
  //   position: fixed;
  //   top: 20px;
  //   ${isRTL ? 'right' : 'left'}: 20px;
  //   background: linear-gradient(135deg, #10b981, #059669);
  //   color: white;
  //   padding: 12px 20px;
  //   border-radius: 12px;
  //   box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
  //   z-index: 10000;
  //   display: flex;
  //   align-items: center;
  //   gap: 10px;
  //   font-weight: 600;
  //   animation: slideIn 0.3s ease;
  //   font-family: inherit;
  // `;

  //   notification.innerHTML = `
  //   <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
  //     <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
  //     <polyline points="22 4 12 14.01 9 11.01"></polyline>
  //   </svg>
  //   ${language === 'ar' ? 'تم تحديث طلبات الإجازات ✨' : 'Leave requests updated ✨'}
  // `;

  //   document.body.appendChild(notification);

  //   setTimeout(() => {
  //     notification.style.animation = 'slideOut 0.3s ease';
  //     setTimeout(() => notification.remove(), 300);
  //   }, 3000);
  // };

  const calculateStats = (leaves) => {
    const pending = leaves.filter((l) => l.status === "pending").length;
    const approved = leaves.filter((l) => l.status === "approved").length;
    const rejected = leaves.filter((l) => l.status === "rejected").length;

    setStats({
      pending,
      approved,
      rejected,
      total: leaves.length,
    });
  };

  const applyFilters = (leaves = leaveRequests) => {
    let filtered = leaves.map((leave) => ({
      ...leave,
      status: normalizeStatusValue(leave.status),
      leaveType: normalizeLeaveTypeValue(leave.leaveType || leave.type),
    }));

    if (filterStatus !== "all") {
      filtered = filtered.filter((leave) => leave.status === filterStatus);
    }

    if (filterType !== "all") {
      filtered = filtered.filter(
        (leave) => (leave.leaveType || leave.type) === filterType
      );
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (leave) =>
          (leave.employeeName || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (leave.reason || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Separate WFH and regular leave requests
    const wfh = filtered.filter((leave) => (leave.leaveType || leave.type) === 'wfh');
    const regular = filtered.filter((leave) => (leave.leaveType || leave.type) !== 'wfh');

    setFilteredRequests(regular);
    setWfhRequests(wfh);
  };

  useEffect(() => {
    applyFilters();
  }, [searchTerm, filterStatus, filterType, leaveRequests]);

  // ✅ Handle submit decision - UPDATED to use apiFetch
  const handleSubmitDecision = async () => {
    if (!decision || !selectedLeave) {
      setAlert({
        open: true,
        title: language === 'ar' ? 'تنبيه' : 'Alert',
        message: language === 'ar' ? 'الرجاء اختيار موافقة أو رفض' : 'Please select approve or reject',
        variant: 'error'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const currentViewType = getCurrentViewType();
      const payload = {
        status: decision,
        approvedBy: profile?.employeeName || localStorage.getItem("employeeName") || "Admin",
        comments: comments.trim()
      };

      const updateLeaveUrl = `${BASE_URL}/api/leaves/${selectedLeave.id}?viewType=${encodeURIComponent(currentViewType)}`;

      // ✅ Use apiFetch instead of fetch
      const response = await apiFetch(updateLeaveUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-View-Type': currentViewType,
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setAlert({
          open: true,
          title: language === 'ar' ? 'نجاح' : 'Success',
          message: language === 'ar'
            ? `تم ${decision === 'approved' ? 'الموافقة على' : 'رفض'} طلب الإجازة بنجاح!`
            : `Leave request ${decision} successfully!`,
          variant: 'success'
        });
        await fetchLeaveRequests();
        setShowDetailsModal(false);
        setDecision("");
        setComments("");
        setSelectedLeave(null);
      } else {
        throw new Error(result.error || result.message || 'Failed to submit decision');
      }

    } catch (error) {
      console.error('Error submitting decision:', error);
      setAlert({
        open: true,
        title: language === 'ar' ? 'خطأ' : 'Error',
        message: language === 'ar'
          ? `فشل إرسال القرار: ${error.message}`
          : `Failed to submit decision: ${error.message}`,
        variant: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  useEffect(() => {
    fetchLeaveRequests(false);

    if (!autoRefreshEnabled) return;

    const intervalId = setInterval(() => {
      console.log('🔄 Auto-refreshing leave requests...');
      fetchLeaveRequests(true);
    }, 54000000); // 15 ساعة = 15 * 60 * 60 * 1000 milliseconds

    return () => clearInterval(intervalId);
  }, [autoRefreshEnabled]);

  useEffect(() => {
    const target = window.__notificationTarget;
    if (target?.leaveId && leaveRequests.length > 0) {
      const leave = leaveRequests.find(l => l.id === Number(target.leaveId));
      if (leave) {
        setSelectedLeave(leave);
        setShowDetailsModal(true);
        setDecision("");
        setComments("");
        window.__notificationTarget = null;
      }
    }
  }, [leaveRequests]);
  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return theme === "dark" ? "#10b981" : "#059669";
      case "pending":
        return theme === "dark" ? "#f59e0b" : "#d97706";
      case "rejected":
        return theme === "dark" ? "#ef4444" : "#dc2626";
      default:
        return theme === "dark" ? "#2e7cba" : "#2e7cba";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return CheckCircle;
      case "pending":
        return Clock;
      case "rejected":
        return XCircle;
      default:
        return AlertCircle;
    }
  };

  const getLeaveTypeIcon = (type) => {
    switch (type) {
      case "annual":
        return Sun;
      case "sick":
        return Heart;
      case "maternity":
        return Home;
      case "emergency":
        return AlertCircle;
      case "wfh":
        return Home;
      default:
        return Calendar;
    }
  };

  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: t.pending,
      approved: t.approved,
      rejected: t.rejected,
    };
    return statusMap[status] || status;
  };

  const getLeaveTypeText = (type) => {
    const typeMap = {
      annual: t.annual,
      sick: t.sick,
      maternity: t.maternity,
      emergency: t.emergency,
      wfh: t.wfh,
    };
    return typeMap[type] || type;
  };

  const LeaveRequestItem = ({ leave }) => {
    const normalizedStatus = normalizeStatusValue(leave.status);
    const normalizedType = normalizeLeaveTypeValue(leave.leaveType || leave.type);
    const StatusIcon = getStatusIcon(normalizedStatus);
    const TypeIcon = getLeaveTypeIcon(normalizedType);
    const duration = leave.days || calculateDuration(leave.startDate, leave.endDate);

    return (
      <div
        style={styles.requestItem}
        onClick={() => {
          setSelectedLeave(leave);
          setShowDetailsModal(true);
          setDecision("");
          setComments("");
        }}
        className="request-item"
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
            flexDirection: isRTL ? "row" : "row",
          }}
        >
          <div
            style={{
              width: "clamp(48px, 8vw, 56px)",
              height: "clamp(48px, 8vw, 56px)",
              background: `${getStatusColor(normalizedStatus)}20`,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <TypeIcon
              size={window.innerWidth < 768 ? 20 : 24}
              color={getStatusColor(normalizedStatus)}
            />
          </div>

          <div style={{ flex: 1, minWidth: "200px" }}>
            <h4
              style={{
                color: theme === "dark" ? "white" : "#1a1a1a",
                fontSize: "clamp(16px, 2.5vw, 18px)",
                fontWeight: "600",
                margin: "0 0 8px 0",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {leave.employeeName || t.unknownEmployee}
            </h4>
            <p
              style={{
                color: theme === "dark"
                  ? "rgba(255, 255, 255, 0.7)"
                  : "rgba(26, 26, 26, 0.7)",
                fontSize: "14px",
                margin: "0 0 4px 0",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {getLeaveTypeText(normalizedType || "unknown")} {" "}
              {t.leave} • {duration} {t.days}
            </p>
            <p
              style={{
                color: theme === "dark"
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.6)",
                fontSize: "13px",
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flexDirection: isRTL ? "row" : "row",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              <Calendar size={12} />
              {new Date(leave.startDate).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')} -{" "}
              {new Date(leave.endDate).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}
            </p>
          </div>

          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexDirection: isRTL ? "row" : "row",
          }}>
            <span
              style={{
                padding: "6px 12px",
                background: `${getStatusColor(normalizedStatus)}20`,
                color: getStatusColor(normalizedStatus),
                borderRadius: "8px",
                fontSize: "12px",
                fontWeight: "600",
                textTransform: "capitalize",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                flexDirection: isRTL ? "row" : "row",
              }}
            >
              <StatusIcon size={14} />
              {getStatusText(normalizedStatus)}
            </span>
            <ChevronRight
              size={20}
              color={
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.4)"
                  : "rgba(26, 26, 26, 0.4)"
              }
              style={{ transform: isRTL ? "rotate(180deg)" : "none" }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div >
      {/* Header */}
      <div style={styles.statCard} className="hover-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
          <div>
            <h1 style={styles.welcomeTitle}>
              <FileText size={window.innerWidth < 768 ? 24 : 28} style={{ display: 'inline-block', verticalAlign: 'middle' }} />
              {' '}{t.leaveManagement}
            </h1>
            <p style={styles.welcomeSubtitle}>{t.reviewAndApprove}</p>
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
            {(userRole === "admin" || userRole === "superadmin") && (
              <button
                onClick={openHolidayModal}
                style={{
                  background: "linear-gradient(135deg, #059669, #047857)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  padding: "12px 20px",
                  fontSize: "14px",
                  fontWeight: "700",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 4px 16px rgba(5,150,105,0.35)",
                  transition: "all 0.3s ease",
                  whiteSpace: "nowrap",
                }}
              >
                <Calendar size={18} />
                {t.holidayManagement}
              </button>
            )}
            <button
              onClick={openManageLeaveModal}
              style={{
                background: "linear-gradient(135deg, #2e7cba, #1a5fa0)",
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                padding: "12px 20px",
                fontSize: "14px",
                fontWeight: "700",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                boxShadow: "0 4px 16px rgba(46,124,186,0.35)",
                transition: "all 0.3s ease",
                whiteSpace: "nowrap",
              }}
            >
              <PlusCircle size={18} />
              {t.manageEmployeeLeaves}
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div style={styles.errorAlert}>
          <AlertCircle size={24} />
          <div style={{ textAlign: isRTL ? "right" : "left" }}>
            <div style={{ fontWeight: '700', marginBottom: '4px' }}>{t.errorLoadingRequests}</div>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>{error}</div>
          </div>
        </div>
      )}

      {/* Statistics */}
      <div style={styles.statCard}>
        <div style={styles.statsGrid}>
          {[
            {
              label: t.pendingRequests,
              value: stats.pending,
              status: "pending",
            },
            { label: t.approved, value: stats.approved, status: "approved" },
            { label: t.rejected, value: stats.rejected, status: "rejected" },
            { label: t.totalRequests, value: stats.total, color: "#2e7cba" },
          ].map((stat, index) => (
            <div key={index} style={styles.statBox}>
              <div
                style={{
                  fontSize: "clamp(28px, 5vw, 32px)",
                  fontWeight: "800",
                  marginBottom: "8px",
                  color: stat.color || getStatusColor(stat.status),
                }}
              >
                {stat.value}
              </div>
              <div
                style={{
                  color: theme === "dark"
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(26, 26, 26, 0.6)",
                  fontSize: "clamp(12px, 1.5vw, 14px)",
                  fontWeight: "600",
                  textTransform: "uppercase",
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: theme === 'dark'
            ? '1px solid rgba(255,255,255,0.1)'
            : '1px solid rgba(0,0,0,0.1)',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          {/* Last Refresh */}
          <div style={{
            fontSize: '12px',
            color: theme === 'dark'
              ? 'rgba(255,255,255,0.6)'
              : 'rgba(26,26,26,0.6)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Clock size={14} />
            <span>
              {language === 'ar' ? 'آخر تحديث:' : 'Last updated:'} {' '}
              {lastRefresh.toLocaleTimeString(language === 'ar' ? 'ar-EG' : 'en-US')}
            </span>
            {isAutoRefreshing && (
              <Loader size={14} className="spin" style={{ marginLeft: '8px' }} />
            )}
          </div>

          {/* Auto-refresh Toggle */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            {/* <button
              onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
              style={{
                background: autoRefreshEnabled
                  ? 'linear-gradient(135deg, #10b981, #059669)'
                  : theme === 'dark'
                    ? 'rgba(255,255,255,0.1)'
                    : 'rgba(0,0,0,0.05)',
                color: autoRefreshEnabled ? 'white' : theme === 'dark' ? '#fff' : '#374151',
                border: 'none',
                borderRadius: '8px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.3s ease',
              }}
            >
              <RefreshCw size={14} />
              {language === 'ar'
                ? (autoRefreshEnabled ? 'التحديث التلقائي: مفعّل' : 'التحديث التلقائي: معطّل')
                : (autoRefreshEnabled ? 'Auto-refresh: ON' : 'Auto-refresh: OFF')}
            </button> */}
          </div>
        </div>
      </div>














      {/* Tabs */}
      <div>
        <div style={styles.tabsContainer}>
          {["pending", "approved", "rejected"].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setFilterStatus(tab);
              }}
              style={{
                ...styles.tabButton,
                ...(activeTab === tab ? styles.activeTabButton : {}),
              }}
              className="tab-button"
            >
              {getStatusText(tab)}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div style={styles.statCard}>
        <div style={styles.controlsHeader}>
          <div>
            <h3 style={styles.controlsTitle}>
              <Settings size={window.innerWidth < 768 ? 16 : 20} />
              {t.searchAndFilter}
            </h3>
            <p style={styles.controlsSubtitle}>
              {t.findAndFilter}
            </p>
          </div>
          <div style={styles.resultsInfo}>
            <BarChart3 size={window.innerWidth < 768 ? 14 : 16} />
            {t.showing} {filteredRequests.length} {t.of} {leaveRequests.length} {t.requests}
          </div>
        </div>

        <div style={styles.controlsGrid}>
          <div style={styles.controlGroup}>
            <label style={styles.label}>
              <Search size={window.innerWidth < 768 ? 14 : 16} />
              {t.searchEmployee}
            </label>
            <div style={styles.inputWrapper}>
              <input
                type="text"
                placeholder={t.searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  ...styles.input,
                  paddingLeft: isRTL ? 'clamp(16px, 3vw, 20px)' : 'clamp(40px, 7vw, 50px)',
                  paddingRight: isRTL ? 'clamp(40px, 7vw, 50px)' : 'clamp(16px, 3vw, 20px)',
                  //marginLeft:100,
                  //                   marginBottom: ,
                  // marginTop: 4,
                }}
                className="enhanced-input"
              />
              <Search
                size={window.innerWidth < 768 ? 16 : 18}
                style={{
                  position: 'absolute',
                  left: isRTL ? 'auto' : (window.innerWidth < 768 ? '12px' : '1px'),
                  right: isRTL ? (window.innerWidth < 768 ? '12px' : '18px') : 'auto',
                  color: theme === "dark" ? "rgba(255,255,255,0.6)" : "#6b7280"
                }}
              />
            </div>
          </div>

          {/* استبدل كود Leave Type القديم بهذا */}
          <div style={{
            ...styles.controlGroup,
            position: "relative",  // ✅ ضيف ده
            zIndex: 999,             // ✅ وده
            isolation: "isolate"  // ✅ ضيف ده عشان يعزل الـ stacking context

          }}>
            <label style={styles.label}>{t.leaveType}</label>
            <div style={{ position: "relative", zIndex: 999 }}>  {/* ✅ وده كمان */}
              <ReusableDropdown
                items={[
                  { id: "all", name: t.allTypes },
                  { id: "annual", name: t.annual },
                  { id: "sick", name: t.sick },
                  { id: "maternity", name: t.maternity },
                  { id: "emergency", name: t.emergency },
                  { id: "wfh", name: t.wfh }
                ]}
                value={filterType}
                onChange={(val) => setFilterType(val)}
                onSelect={(item) => {
                  const value = typeof item === "string"
                    ? item
                    : item?.id || String(item);
                  setFilterType(value);
                }}
                getItemKey={(it) =>
                  typeof it === "string" ? it : it.id || String(it)
                }
                getItemLabel={(it) =>
                  typeof it === "string" ? it : it.name || String(it)
                }
                placeholder={t.selectLeaveType || "اختر نوع الإجازة..."}
                theme={theme}
                language={language}
                maxItems={10}
              />
            </div>
          </div>


          <div style={styles.controlGroup}>
            <label style={styles.label}>
              <RefreshCw size={window.innerWidth < 768 ? 14 : 16} />
              {t.actions}
            </label>
            <button
              onClick={fetchLeaveRequests}
              style={styles.primaryButton}
              //className="enhanced-button primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader size={16} className="spin" />
                  {t.loading}
                </>
              ) : (
                <>
                  <RefreshCw size={16} />
                  {t.refresh}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ==================== REQUESTS CARDS GRID ==================== */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(2, 1fr)',
        gap: 'clamp(16px, 3vw, 24px)',
      }}>
        {/* ==================== LEAVE REQUESTS CARD ==================== */}
        <div style={styles.statCard}>
          <h3
            style={{
              ...styles.controlsTitle,
              marginBottom: "24px",
            }}
          >
            <FileText size={24} />
            {t.leaveRequests} ({filteredRequests.length})
          </h3>

          {isLoading ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 40px",
                color: theme === "dark"
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.5)",
              }}
            >
              <Loader
                size={48}
                className="spin"
                style={{
                  margin: "0 auto 16px",
                }}
              />
              <div>{t.loadingRequests}</div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 40px",
                color: theme === "dark"
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.5)",
              }}
            >
              <FileText
                size={64}
                style={{ opacity: 0.3, margin: "0 auto 16px" }}
              />
              <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '12px' }}>
                {t.noRequestsFound}
              </h3>
              <p style={{ fontSize: '14px' }}>
                {t.tryAdjusting}
              </p>
            </div>
          ) : (
            filteredRequests.map((leave) => (
              <LeaveRequestItem key={leave.id} leave={leave} />
            ))
          )}
        </div>

        {/* ==================== WORK FROM HOME REQUESTS CARD ==================== */}
        <div style={styles.statCard}>
          <h3
            style={{
              ...styles.controlsTitle,
              marginBottom: "24px",
            }}
          >
            <Home size={24} />
            {t.wfh} ({wfhRequests.length})
          </h3>

          {isLoading ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 40px",
                color: theme === "dark"
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.5)",
              }}
            >
              <Loader
                size={48}
                className="spin"
                style={{
                  margin: "0 auto 16px",
                }}
              />
              <div>{t.loadingRequests}</div>
            </div>
          ) : wfhRequests.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 40px",
                color: theme === "dark"
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.5)",
              }}
            >
              <Home
                size={64}
                style={{ opacity: 0.3, margin: "0 auto 16px" }}
              />
              <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '12px' }}>
                {language === 'ar' ? 'لا توجد طلبات عمل من المنزل' : 'No work from home requests'}
              </h3>
              <p style={{ fontSize: '14px' }}>
                {t.tryAdjusting}
              </p>
            </div>
          ) : (
            wfhRequests.map((leave) => (
              <LeaveRequestItem key={leave.id} leave={leave} />
            ))
          )}
        </div>
      </div>

      {/* ==================== MANAGE EMPLOYEE LEAVES MODAL ==================== */}
      {showManageLeaveModal && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9998,
            display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
          }}
          onClick={() => setShowManageLeaveModal(false)}
        >
          <div
            style={{
              background: theme === "dark" ? "#1e2533" : "#ffffff",
              borderRadius: "20px",
              padding: "28px",
              width: "100%",
              maxWidth: "640px",
              maxHeight: "85vh",
              overflowY: "auto",
              boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
              direction: isRTL ? "rtl" : "ltr",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {empModalStep === "manage" && (
                  <button
                    onClick={() => { setEmpModalStep("select"); setSelectedEmpForLeave(null); setEmpLeaves([]); }}
                    style={{ background: "none", border: "none", cursor: "pointer", color: theme === "dark" ? "#fff" : "#374151", padding: "4px", borderRadius: "8px", display: "flex", alignItems: "center" }}
                  >
                    <ChevronRight size={20} style={{ transform: isRTL ? "none" : "rotate(180deg)" }} />
                  </button>
                )}
                <Users size={22} color="#2e7cba" />
                <div>
                  <div style={{ fontSize: "18px", fontWeight: "700", color: theme === "dark" ? "#fff" : "#1a1a1a" }}>
                    {empModalStep === "select" ? t.manageEmployeeLeaves : (selectedEmpForLeave?.name || selectedEmpForLeave?.employeeName || "")}
                  </div>
                  {empModalStep === "select" && (
                    <div style={{ fontSize: "13px", color: theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(26,26,26,0.6)" }}>
                      {t.manageEmployeeLeavesDesc}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowManageLeaveModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: theme === "dark" ? "#fff" : "#1a1a1a", padding: "8px", borderRadius: "8px" }}
              >
                <X size={22} />
              </button>
            </div>

            {/* ── STEP 1: Select an employee ── */}
            {empModalStep === "select" && (
              <div>
                <div style={{ position: "relative", marginBottom: "16px" }}>
                  <Search size={16} style={{ position: "absolute", top: "50%", [isRTL ? "right" : "left"]: "14px", transform: "translateY(-50%)", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af", pointerEvents: "none" }} />
                  <input
                    value={empModalSearch}
                    onChange={(e) => setEmpModalSearch(e.target.value)}
                    placeholder={t.searchEmployeePlaceholder}
                    style={{
                      width: "100%", boxSizing: "border-box",
                      padding: isRTL ? "12px 40px 12px 14px" : "12px 14px 12px 40px",
                      background: theme === "dark" ? "rgba(255,255,255,0.07)" : "#f9fafb",
                      border: theme === "dark" ? "1.5px solid rgba(255,255,255,0.15)" : "1.5px solid rgba(0,0,0,0.12)",
                      borderRadius: "12px", color: theme === "dark" ? "#fff" : "#1a1a1a",
                      fontSize: "14px", outline: "none",
                    }}
                  />
                </div>

                {empModalLoading ? (
                  <div style={{ textAlign: "center", padding: "48px 0", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af" }}>
                    <Loader size={32} className="spin" style={{ margin: "0 auto 12px" }} />
                    <div>{t.loading}</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "400px", overflowY: "auto" }}>
                    {empModalEmployees
                      .filter((emp) => {
                        const name = (emp.name || emp.employeeName || emp.fullName || "").toLowerCase();
                        const code = String(emp.infinityCode || "").toLowerCase();
                        const q = empModalSearch.toLowerCase();
                        return !q || name.includes(q) || code.includes(q);
                      })
                      .map((emp) => (
                        <button
                          key={emp.infinityCode || emp.id}
                          onClick={() => {
                            setSelectedEmpForLeave(emp);
                            setEmpModalStep("manage");
                            fetchEmployeeLeaves(emp.infinityCode);
                            fetchEmpQuota(emp.infinityCode);
                          }}
                          style={{
                            display: "flex", alignItems: "center", gap: "14px",
                            padding: "14px 16px", borderRadius: "12px", border: "none",
                            background: theme === "dark" ? "rgba(255,255,255,0.05)" : "#f9fafb",
                            cursor: "pointer", textAlign: isRTL ? "right" : "left",
                            transition: "background 0.2s",
                            width: "100%",
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = theme === "dark" ? "rgba(46,124,186,0.25)" : "#e8f2fb"}
                          onMouseLeave={(e) => e.currentTarget.style.background = theme === "dark" ? "rgba(255,255,255,0.05)" : "#f9fafb"}
                        >
                          <div style={{ width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg,#2e7cba,#1a5fa0)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: "700", fontSize: "16px", flexShrink: 0 }}>
                            {(emp.sr || emp.name || emp.employeeName || emp.fullName || "?").charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: "600", fontSize: "15px", color: theme === "dark" ? "#fff" : "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {emp.sr || emp.name || emp.employeeName || emp.fullName}
                            </div>
                            <div style={{ fontSize: "12px", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af" }}>
                              #{emp.infinityCode}
                            </div>
                          </div>
                          <ChevronRight size={16} color={theme === "dark" ? "rgba(255,255,255,0.4)" : "#9ca3af"} style={{ transform: isRTL ? "rotate(180deg)" : "none" }} />
                        </button>
                      ))}
                    {empModalEmployees.filter((emp) => {
                      const name = (emp.name || emp.employeeName || emp.fullName || "").toLowerCase();
                      const code = String(emp.infinityCode || "").toLowerCase();
                      const q = empModalSearch.toLowerCase();
                      return !q || name.includes(q) || code.includes(q);
                    }).length === 0 && (
                        <div style={{ textAlign: "center", padding: "40px 0", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af" }}>
                          <User size={40} style={{ margin: "0 auto 12px", opacity: 0.3 }} />
                          <div>{t.noEmployeesFound}</div>
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 2: Manage selected employee's leaves ── */}
            {empModalStep === "manage" && selectedEmpForLeave && (
              <div>
                {/* Existing leaves */}
                <div style={{ marginBottom: "24px" }}>
                  <h4 style={{ margin: "0 0 14px 0", fontWeight: "700", fontSize: "15px", color: theme === "dark" ? "#fff" : "#1a1a1a", display: "flex", alignItems: "center", gap: "8px" }}>
                    <Calendar size={16} color="#2e7cba" />
                    {t.manualLeaves}
                  </h4>
                  {empLeavesLoading ? (
                    <div style={{ textAlign: "center", padding: "32px 0", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af" }}>
                      <Loader size={28} className="spin" style={{ margin: "0 auto 10px" }} />
                      <div>{t.loading}</div>
                    </div>
                  ) : empLeaves.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "32px 0", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af", fontSize: "14px" }}>
                      {t.noLeavesFound}
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "240px", overflowY: "auto" }}>
                      {empLeaves.map((lv) => (
                        <div
                          key={lv.id}
                          style={{
                            display: "flex", alignItems: "center", gap: "12px",
                            padding: "12px 14px", borderRadius: "12px",
                            background: theme === "dark" ? "rgba(255,255,255,0.05)" : "#f9fafb",
                            border: theme === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.06)",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: "600", fontSize: "14px", color: theme === "dark" ? "#fff" : "#1a1a1a", marginBottom: "3px" }}>
                              {getLeaveTypeText(normalizeLeaveTypeValue(lv.leaveType || lv.type))}
                              <span style={{ marginLeft: 8, padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600", background: `${getStatusColor(normalizeStatusValue(lv.status))}20`, color: getStatusColor(normalizeStatusValue(lv.status)) }}>
                                {getStatusText(normalizeStatusValue(lv.status))}
                              </span>
                            </div>
                            <div style={{ fontSize: "12px", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af" }}>
                              {new Date(lv.startDate).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US")} → {new Date(lv.endDate).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US")}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteEmpLeave(lv.id)}
                            disabled={deletingLeaveIds.has(lv.id)}
                            style={{
                              background: "none", border: "1.5px solid #ef4444", borderRadius: "8px",
                              color: "#ef4444", cursor: "pointer", padding: "6px 10px",
                              display: "flex", alignItems: "center", gap: "5px",
                              fontSize: "12px", fontWeight: "600", opacity: deletingLeaveIds.has(lv.id) ? 0.5 : 1,
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => { if (!deletingLeaveIds.has(lv.id)) { e.currentTarget.style.background = "#ef444420"; } }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
                          >
                            {deletingLeaveIds.has(lv.id) ? <Loader size={14} className="spin" /> : <Trash2 size={14} />}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Create new leave form */}
                <div style={{ borderTop: theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)", paddingTop: "20px" }}>
                  <h4 style={{ margin: "0 0 14px 0", fontWeight: "700", fontSize: "15px", color: theme === "dark" ? "#fff" : "#1a1a1a", display: "flex", alignItems: "center", gap: "8px" }}>
                    <PlusCircle size={16} color="#2e7cba" />
                    {t.addNewLeave}
                  </h4>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "14px" }}>
                    <div style={{ position: "relative", zIndex: 500 }}>
                      <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#6b7280", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        {t.leaveType}
                      </label>
                      <ReusableDropdown
                        items={[
                          { id: "annual", name: t.annual },
                          { id: "sick", name: t.sick },
                          { id: "maternity", name: t.maternity },
                          { id: "emergency", name: t.emergency },
                          { id: "partialDay", name: t.partialDay },
                          { id: "wfh", name: t.wfh },
                          { id: "custom", name: t.custom },
                        ]}
                        value={newLeaveForm.leaveType
                          ? ([
                            { id: "annual", name: t.annual },
                            { id: "sick", name: t.sick },
                            { id: "maternity", name: t.maternity },
                            { id: "emergency", name: t.emergency },
                            { id: "partialDay", name: t.partialDay },
                            { id: "wfh", name: t.wfh },
                            { id: "custom", name: t.custom },
                          ].find((o) => o.id === newLeaveForm.leaveType)?.name || "")
                          : ""}
                        onChange={(val) => {
                          // onChange receives the item id directly from ReusableDropdown
                          setNewLeaveForm((f) => ({ ...f, leaveType: val || "", customLeaveType: "" }));
                        }}
                        onSelect={(item) => setNewLeaveForm((f) => ({ ...f, leaveType: item.id, customLeaveType: "" }))}
                        getItemKey={(item) => item.id}
                        getItemLabel={(item) => item.name}
                        placeholder={t.selectLeaveType}
                        theme={theme}
                        language={language}
                        maxItems={10}
                        showAllOnArrowClick={true}
                        showClear={!!newLeaveForm.leaveType}
                        onClear={() => setNewLeaveForm((f) => ({ ...f, leaveType: "", customLeaveType: "" }))}
                        inputStyle={{ fontSize: "14px", padding: "10px 12px", borderRadius: "10px" }}
                      />
                      {/* Balance info for quota-tracked types */}
                      {(() => {
                        const balance = getLeaveTypeBalance(newLeaveForm.leaveType);
                        if (!balance) return null;
                        const isLow = balance.remaining <= 2;
                        return (
                          <div style={{
                            marginTop: "6px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: isLow ? "#ef4444" : (theme === "dark" ? "rgba(255,255,255,0.6)" : "#6b7280"),
                          }}>
                            <span style={{
                              display: "inline-block",
                              width: 8, height: 8, borderRadius: "50%",
                              background: isLow ? "#ef4444" : "#10b981",
                              flexShrink: 0,
                            }} />
                            {t.balanceRemaining}: {empQuotaLoading ? "..." : `${balance.remaining} ${t.days}`}
                            <span style={{ opacity: 0.6 }}>{t.quotaLabel} {balance.total}</span>
                          </div>
                        );
                      })()}
                      {newLeaveForm.leaveType === "custom" && (
                        <input
                          type="text"
                          value={newLeaveForm.customLeaveType}
                          onChange={(e) => setNewLeaveForm((f) => ({ ...f, customLeaveType: e.target.value }))}
                          placeholder={t.customTypePlaceholder}
                          style={{
                            marginTop: "8px",
                            width: "100%", boxSizing: "border-box", padding: "10px 12px",
                            background: theme === "dark" ? "rgba(255,255,255,0.07)" : "#f9fafb",
                            border: theme === "dark" ? "1.5px solid rgba(255,255,255,0.15)" : "1.5px solid rgba(0,0,0,0.12)",
                            borderRadius: "10px", color: theme === "dark" ? "#fff" : "#1a1a1a",
                            fontSize: "14px", outline: "none",
                          }}
                        />
                      )}
                    </div>
                    {newLeaveForm.leaveType === "partialDay" ? (
                      <>
                        <div>
                          <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#6b7280", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            {t.date}
                          </label>
                          <input
                            type="date"
                            value={newLeaveForm.startDate}
                            onChange={(e) => setNewLeaveForm((f) => ({ ...f, startDate: e.target.value }))}
                            style={{
                              width: "100%", boxSizing: "border-box", padding: "10px 12px",
                              background: theme === "dark" ? "rgba(255,255,255,0.07)" : "#f9fafb",
                              border: theme === "dark" ? "1.5px solid rgba(255,255,255,0.15)" : "1.5px solid rgba(0,0,0,0.12)",
                              borderRadius: "10px", color: theme === "dark" ? "#fff" : "#1a1a1a",
                              fontSize: "14px", outline: "none",
                              colorScheme: theme === "dark" ? "dark" : "light",
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#6b7280", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            {t.halfDayType}
                          </label>
                          <div style={{ position: 'relative', zIndex: 600 }}>
                            <ReusableDropdown
                              items={[
                                { id: '', name: t.selectHalfDayType },
                                { id: 'first_half', name: t.firstHalf },
                                { id: 'second_half', name: t.secondHalf },
                              ]}
                              value={newLeaveForm.halfDayType}
                              onChange={(id) => setNewLeaveForm((f) => ({ ...f, halfDayType: id }))}
                              placeholder={t.selectHalfDayType}
                              emptyMessage={t.selectHalfDayType}
                              maxItems={10}
                              theme={theme}
                              language={language}
                              showAllOnArrowClick={false}
                              showClear={false}
                              inputStyle={{ padding: '10px 12px', borderRadius: '10px', border: theme === 'dark' ? '1.5px solid rgba(255,255,255,0.15)' : '1.5px solid rgba(0,0,0,0.12)', background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#f9fafb', color: theme === 'dark' ? '#fff' : '#1a1a1a' }}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#6b7280", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            {t.startDate}
                          </label>
                          <input
                            type="date"
                            value={newLeaveForm.startDate}
                            onChange={(e) => setNewLeaveForm((f) => ({ ...f, startDate: e.target.value }))}
                            style={{
                              width: "100%", boxSizing: "border-box", padding: "10px 12px",
                              background: theme === "dark" ? "rgba(255,255,255,0.07)" : "#f9fafb",
                              border: theme === "dark" ? "1.5px solid rgba(255,255,255,0.15)" : "1.5px solid rgba(0,0,0,0.12)",
                              borderRadius: "10px", color: theme === "dark" ? "#fff" : "#1a1a1a",
                              fontSize: "14px", outline: "none",
                              colorScheme: theme === "dark" ? "dark" : "light",
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#6b7280", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            {t.endDate}
                          </label>
                          <input
                            type="date"
                            value={newLeaveForm.endDate}
                            onChange={(e) => setNewLeaveForm((f) => ({ ...f, endDate: e.target.value }))}
                            style={{
                              width: "100%", boxSizing: "border-box", padding: "10px 12px",
                              background: theme === "dark" ? "rgba(255,255,255,0.07)" : "#f9fafb",
                              border: theme === "dark" ? "1.5px solid rgba(255,255,255,0.15)" : "1.5px solid rgba(0,0,0,0.12)",
                              borderRadius: "10px", color: theme === "dark" ? "#fff" : "#1a1a1a",
                              fontSize: "14px", outline: "none",
                              colorScheme: theme === "dark" ? "dark" : "light",
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={handleCreateEmpLeave}
                    disabled={createLeaveLoading}
                    style={{
                      background: createLeaveLoading ? "rgba(46,124,186,0.5)" : "linear-gradient(135deg,#2e7cba,#1a5fa0)",
                      color: "#fff", border: "none", borderRadius: "12px",
                      padding: "12px 24px", fontSize: "14px", fontWeight: "700",
                      cursor: createLeaveLoading ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "8px",
                      boxShadow: createLeaveLoading ? "none" : "0 4px 16px rgba(46,124,186,0.3)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {createLeaveLoading ? <><Loader size={16} className="spin" />{t.creating}</> : <><PlusCircle size={16} />{t.createLeave}</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedLeave && (
        <div style={styles.modal} onClick={() => setShowDetailsModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              flexDirection: isRTL ? 'row' : 'row',
              scrollbarWidth: "none", // للـ Firefox
              msOverflowStyle: "none", // للـ IE و Edge
              WebkitOverflowScrolling: "touch", // للـ iOS
              scrollbarWidth: "none", // للـ Firefox
              msOverflowStyle: "none", // للـ IE و Edge
            }}>
              <div style={{
                fontSize: 'clamp(18px, 3vw, 24px)',
                fontWeight: '700',
                scrollbarWidth: "none", // للـ Firefox
                msOverflowStyle: "none", // للـ IE و Edge
                WebkitOverflowScrolling: "touch", // للـ iOS
                scrollbarWidth: "none", // للـ Firefox
                msOverflowStyle: "none", // للـ IE و Edge
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexDirection: isRTL ? 'row' : 'row',
              }}>
                <FileText size={24} color="#2e7cba" />
                {t.leaveRequestReview}
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: theme === 'dark' ? 'white' : '#1a1a1a',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '8px',
                  transition: 'background 0.2s',
                }}
                className="close-button"
              >
                <X size={24} />
              </button>
            </div>

            {/* Employee Info */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                flexDirection: isRTL ? 'row' : 'row',
              }}>
                <User size={18} />
                <div style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  flex: 1,
                  textAlign: isRTL ? 'right' : 'left',
                }}>
                  {t.employeeInformation}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: 'clamp(16px, 3vw, 20px)',
                  background: theme === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)',
                  borderRadius: '12px',
                  border: theme === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                  flexDirection: isRTL ? 'row' : 'row',
                }}
              >
                <div
                  style={{
                    width: 'clamp(48px, 8vw, 56px)',
                    height: 'clamp(48px, 8vw, 56px)',
                    background: 'linear-gradient(135deg, #2e7cba, #2e7cba)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 'clamp(20px, 3vw, 24px)',
                    fontWeight: '700',
                    flexShrink: 0,
                  }}
                >
                  {selectedLeave.employeeName?.charAt(0).toUpperCase()}
                </div>
                <div style={{ textAlign: isRTL ? 'right' : 'left' }}>
                  <div
                    style={{
                      fontSize: 'clamp(18px, 2.5vw, 20px)',
                      fontWeight: '700',
                      marginBottom: '4px',
                    }}
                  >
                    {selectedLeave.employeeName}
                  </div>
                  <div
                    style={{
                      fontSize: '14px',
                      color: theme === 'dark'
                        ? 'rgba(255, 255, 255, 0.7)'
                        : 'rgba(26, 26, 26, 0.7)',
                    }}
                  >
                    {t.requestedOn}{" "}
                    {new Date(
                      selectedLeave.created_at || selectedLeave.requestDate
                    ).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}
                  </div>
                </div>
              </div>
            </div>

            {/* Request Details */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                flexWrap: 'wrap',
                flexDirection: isRTL ? 'row' : 'row',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexDirection: isRTL ? 'row' : 'row' }}>
                  <Calendar size={18} />
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                  }}>
                    {t.requestDetails}
                  </div>
                </div>
                <span
                  style={{
                    background: theme === 'dark'
                      ? `${getStatusColor(
                        selectedLeave.leaveType || selectedLeave.type
                      )}20`
                      : `${getStatusColor(
                        selectedLeave.leaveType || selectedLeave.type
                      )}15`,
                    color: getStatusColor(
                      selectedLeave.leaveType || selectedLeave.type
                    ),
                    padding: '4px 12px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'capitalize',
                  }}
                >
                  {getLeaveTypeText(selectedLeave.leaveType || selectedLeave.type)}
                </span>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(2, 1fr)',
                gap: '16px',
              }}>
                <div style={{
                  background: theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
                  border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '12px',
                  padding: '16px',
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.6)',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    marginBottom: '8px',
                    textAlign: isRTL ? 'right' : 'left',
                  }}>
                    {t.startDate}
                  </div>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    lineHeight: '1.5',
                    textAlign: isRTL ? 'right' : 'left',
                  }}>
                    {new Date(selectedLeave.startDate).toLocaleDateString(
                      language === 'ar' ? 'ar-EG' : 'en-US',
                      {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      }
                    )}
                  </div>
                </div>

                <div style={{
                  background: theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
                  border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '12px',
                  padding: '16px',
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.6)',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    marginBottom: '8px',
                    textAlign: isRTL ? 'right' : 'left',
                  }}>
                    {t.endDate}
                  </div>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    lineHeight: '1.5',
                    textAlign: isRTL ? 'right' : 'left',
                  }}>
                    {new Date(selectedLeave.endDate).toLocaleDateString(
                      language === 'ar' ? 'ar-EG' : 'en-US',
                      {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      }
                    )}
                  </div>
                </div>

                <div style={{
                  background: theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
                  border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '12px',
                  padding: '16px',
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.6)',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    marginBottom: '8px',
                    textAlign: isRTL ? 'right' : 'left',
                  }}>
                    {t.duration}
                  </div>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    lineHeight: '1.5',
                    textAlign: isRTL ? 'right' : 'left',
                  }}>
                    {selectedLeave.days ||
                      calculateDuration(
                        selectedLeave.startDate,
                        selectedLeave.endDate
                      )}{" "}
                    {t.days}
                  </div>
                </div>

                <div style={{
                  background: theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
                  border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '12px',
                  padding: '16px',
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.6)',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    marginBottom: '8px',
                    textAlign: isRTL ? 'right' : 'left',
                  }}>
                    {t.status}
                  </div>
                  <span
                    style={{
                      padding: '6px 12px',
                      background: `${getStatusColor(selectedLeave.status)}20`,
                      color: getStatusColor(selectedLeave.status),
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'capitalize',
                      display: 'inline-block',
                    }}
                  >
                    {getStatusText(selectedLeave.status)}
                  </span>
                </div>
              </div>

              {selectedLeave.reason && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: 'clamp(16px, 3vw, 20px)',
                    background: theme === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.03)',
                    borderRadius: '12px',
                    border: theme === 'dark'
                      ? '2px solid rgba(255, 255, 255, 0.08)'
                      : '2px solid rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <div
                    style={{
                      fontSize: '12px',
                      color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.6)',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexDirection: isRTL ? 'row' : 'row',
                      textAlign: isRTL ? 'right' : 'left',
                    }}
                  >
                    <FileText size={14} />
                    {t.reasonForLeave}
                  </div>
                  <p
                    style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      lineHeight: '1.7',
                      margin: 0,
                      textAlign: isRTL ? 'right' : 'left',
                    }}
                  >
                    {selectedLeave.reason}
                  </p>
                </div>
              )}
            </div>

            {/* Decision Section */}
            {selectedLeave.status === 'pending' && (() => {
              const { hasApprovePermission, hasRejectPermission, hasAnyPermission } = getLeaveActionPermissions(selectedLeave);

              return hasAnyPermission ? (
                <div style={{ marginBottom: '24px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                    paddingBottom: '12px',
                    borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                    flexDirection: isRTL ? 'row' : 'row',
                  }}>
                    <CheckCircle size={18} />
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '700',
                    }}>
                      {t.yourDecision}
                    </div>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '12px',
                    marginBottom: '16px',
                  }}>
                    {hasApprovePermission && (
                      <button
                        style={{
                          padding: '16px 20px',
                          border: `2px solid ${decision === 'approved'
                            ? getStatusColor('approved')
                            : theme === 'dark'
                              ? 'rgba(255,255,255,0.2)'
                              : 'rgba(0,0,0,0.2)'
                            }`,
                          borderRadius: '12px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '10px',
                          transition: 'all 0.3s ease',
                          background:
                            decision === 'approved'
                              ? `linear-gradient(135deg, ${getStatusColor(
                                'approved'
                              )}, ${getStatusColor('approved')}dd)`
                              : 'transparent',
                          color:
                            decision === 'approved'
                              ? 'white'
                              : theme === 'dark'
                                ? 'rgba(255,255,255,0.7)'
                                : 'rgba(26,26,26,0.7)',
                          boxShadow:
                            decision === 'approved'
                              ? `0 8px 24px ${getStatusColor('approved')}40`
                              : 'none',
                          flexDirection: isRTL ? 'row' : 'row',
                        }}
                        onClick={() => setDecision('approved')}
                        disabled={isSubmitting}
                      >
                        <CheckCircle size={20} />
                        {t.approve}
                      </button>
                    )}
                    {hasRejectPermission && (
                      <button
                        style={{
                          padding: '16px 20px',
                          border: `2px solid ${decision === 'rejected'
                            ? getStatusColor('rejected')
                            : theme === 'dark'
                              ? 'rgba(255,255,255,0.2)'
                              : 'rgba(0,0,0,0.2)'
                            }`,
                          borderRadius: '12px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '10px',
                          transition: 'all 0.3s ease',
                          background:
                            decision === 'rejected'
                              ? `linear-gradient(135deg, ${getStatusColor(
                                'rejected'
                              )}, ${getStatusColor('rejected')}dd)`
                              : 'transparent',
                          color:
                            decision === 'rejected'
                              ? 'white'
                              : theme === 'dark'
                                ? 'rgba(255,255,255,0.7)'
                                : 'rgba(26,26,26,0.7)',
                          boxShadow:
                            decision === 'rejected'
                              ? `0 8px 24px ${getStatusColor('rejected')}40`
                              : 'none',
                          flexDirection: isRTL ? 'row' : 'row',
                        }}
                        onClick={() => setDecision('rejected')}
                        disabled={isSubmitting}
                      >
                        <XCircle size={20} />
                        {t.reject}
                      </button>
                    )}
                  </div>

                  {decision && (
                    <div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.6)',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.8px',
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          flexDirection: isRTL ? 'row' : 'row',
                          textAlign: isRTL ? 'right' : 'left',
                        }}
                      >
                        <FileText size={14} />
                        {decision === 'approved'
                          ? t.approvalComments
                          : t.reasonForRejection}{' '}
                        {t.optional}
                      </div>
                      <textarea
                        style={styles.textarea}
                        placeholder={
                          decision === 'approved'
                            ? t.approvalCommentsPlaceholder
                            : t.rejectionReasonPlaceholder
                        }
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        disabled={isSubmitting}
                        className="enhanced-input"
                      />
                    </div>
                  )}
                </div>
              ) : null;
            })()}

            {/* Action Buttons */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px',
                paddingTop: '16px',
                borderTop: theme === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.1)',
                flexWrap: 'wrap',
                flexDirection: isRTL ? 'row' : 'row',
              }}
            >
              <button
                style={styles.secondaryButton}
                onClick={() => {
                  setShowDetailsModal(false);
                  setDecision('');
                  setComments('');
                }}
                disabled={isSubmitting}
                className="enhanced-button secondary"
              >
                {t.cancel}
              </button>
              {selectedLeave.status === 'pending' && (() => {
                const { hasApprovePermission, hasRejectPermission } = getLeaveActionPermissions(selectedLeave);
                const canSubmit = (decision === 'approved' && hasApprovePermission) || (decision === 'rejected' && hasRejectPermission);

                return (hasApprovePermission || hasRejectPermission) && canSubmit ? (
                  <button
                    style={{
                      ...styles.primaryButton,
                      opacity: !decision || isSubmitting ? 0.5 : 1,
                    }}
                    onClick={handleSubmitDecision}
                    disabled={!decision || isSubmitting}
                    className="enhanced-button primary"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader
                          size={18}
                          className="spin"
                        />
                        {t.processing}
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        {decision === 'approved'
                          ? t.submitApproval
                          : decision === 'rejected'
                            ? t.submitRejection
                            : t.submitDecision}
                      </>
                    )}
                  </button>
                ) : null;
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Global Styles */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          .spin {
            animation: spin 1s linear infinite;
          }

          .hover-card {
            cursor: default;
          }
            
          
          .hover-card:hover {
            transform: translateY(clamp(-3px, -0.5vw, -5px));
            box-shadow: ${theme === "dark"
            ? "0 12px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)"
            : "0 12px 40px rgba(0,0,0,0.15)"} !important;
          }

          .request-item:hover {
            background: ${theme === "dark"
            ? "rgba(255, 255, 255, 0.08)"
            : "rgba(0, 0, 0, 0.05)"} !important;
            transform: translate${isRTL ? 'X(-4px)' : 'X(4px)'};
          }

          .enhanced-input:focus, .enhanced-select:focus {
            border-color: #2e7cba !important;
            box-shadow: 0 0 0 clamp(2px, 0.5vw, 4px) rgba(59, 130, 246, 0.15) !important;
          }

          .enhanced-button:hover:not(:disabled) {
            transform: translateY(clamp(-1px, -0.2vw, -2px)) scale(1.02);
          }

          .enhanced-button.primary:hover:not(:disabled) {
            box-shadow: 0 15px 40px rgba(59, 130, 246, 0.4) !important;
          }

          .tab-button:hover {
            background: ${theme === "dark"
            ? "rgba(255,255,255,0.1)"
            : "rgba(0,0,0,0.05)"};
          }

          .close-button:hover {
            background: ${theme === "dark"
            ? "rgba(255,255,255,0.1)"
            : "rgba(0,0,0,0.05)"} !important;
          }

          ::-webkit-scrollbar {
            width: clamp(6px, 1vw, 8px);
            height: clamp(6px, 1vw, 8px);
          }
          
          ::-webkit-scrollbar-track {
            background: ${theme === "dark"
            ? "rgba(255,255,255,0.05)"
            : "rgba(0,0,0,0.05)"};
            border-radius: 10px;
          }
            .reusable-dropdown-container {
      position: relative !important;
      z-index: 100 !important;
    }
    
    .reusable-dropdown-menu {
      position: absolute !important;
      z-index: 100 !important;
    }
          ::-webkit-scrollbar-thumb {
            background: ${theme === "dark"
            ? "rgba(255,255,255,0.2)"
            : "rgba(0,0,0,0.2)"};
            border-radius: 10px;
          }

          * {
            -webkit-tap-highlight-color: transparent;
            box-sizing: border-box;
          }

          input::placeholder, textarea::placeholder {
            color: ${theme === "dark"
            ? "rgba(255, 255, 255, 0.5)"
            : "rgba(26, 26, 26, 0.5)"};
          }

          button:active:not(:disabled) {
            transform: translateY(0);
          }

          @media (max-width: 768px) {
            .request-item:hover {
              transform: none;
            }
          }
        `}
      </style>

      <AlertModal
        open={alert.open}
        title={alert.title}
        message={alert.message}
        variant={alert.variant}
        onClose={() => setAlert({ ...alert, open: false })}
        theme={theme}
        language={language}
      />

      {/* ==================== HOLIDAY MANAGEMENT MODAL ==================== */}
      {showHolidayModal && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.65)",
            backdropFilter: "blur(6px)",
            zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
          }}
          onClick={() => setShowHolidayModal(false)}
        >
          <div
            style={{
              background: theme === "dark" ? "#1e2533" : "#ffffff",
              borderRadius: "24px",
              padding: "32px",
              width: "100%",
              maxWidth: "860px",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 32px 80px rgba(0,0,0,0.4)",
              direction: isRTL ? "rtl" : "ltr",
              scrollbarWidth: "none", // للـ Firefox
              msOverflowStyle: "none", // للـ IE و Edge
              WebkitOverflowScrolling: "touch", // للـ iOS
              scrollbarWidth: "none", // للـ Firefox
              msOverflowStyle: "none", // للـ IE و Edge
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px", scrollbarWidth: "none", // للـ Firefox
              msOverflowStyle: "none", // للـ IE و Edge
              WebkitOverflowScrolling: "touch", // للـ iOS
              scrollbarWidth: "none", // للـ Firefox
              msOverflowStyle: "none",
            }}>
              <div style={{
                display: "flex", scrollbarWidth: "none", // للـ Firefox
                msOverflowStyle: "none", // للـ IE و Edge
                WebkitOverflowScrolling: "touch", // للـ iOS
                scrollbarWidth: "none", // للـ Firefox
                msOverflowStyle: "none", alignItems: "center", gap: "12px"
              }}>
                <div style={{ background: "linear-gradient(135deg,#059669,#047857)", borderRadius: "12px", padding: "10px", display: "flex" }}>
                  <Calendar size={22} color="#fff" />
                </div>
                <div>
                  <div style={{ fontSize: "20px", fontWeight: "800", color: theme === "dark" ? "#fff" : "#111827" }}>
                    {t.holidayManagement}
                  </div>
                  <div style={{ fontSize: "13px", color: theme === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)", marginTop: "2px" }}>
                    {t.holidayManagementDesc}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowHolidayModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: theme === "dark" ? "#fff" : "#374151", padding: "8px", borderRadius: "8px" }}
              >
                <X size={22} />
              </button>
            </div>

            {/* ── Create / Edit Form ── */}
            <div style={{
              background: theme === "dark" ? "rgba(255,255,255,0.04)" : "#f0fdf4",
              border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(5,150,105,0.2)"}`,
              borderRadius: "16px",
              padding: "24px",
              marginBottom: "28px",
            }}>
              <div style={{ fontSize: "15px", fontWeight: "700", color: theme === "dark" ? "#34d399" : "#059669", marginBottom: "18px", display: "flex", alignItems: "center", gap: "8px" }}>
                {editingHoliday ? <><Settings size={16} /> {t.editHoliday}</> : <><PlusCircle size={16} /> {t.createHoliday}</>}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px" }}>
                {/* Client Name — always read-only, auto-filled from admin's client */}
                <div>
                  <label style={{ fontSize: "12px", fontWeight: "700", color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#374151", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "6px" }}>
                    {t.clientNameLabel} *
                  </label>
                  <div style={{ padding: "11px 14px", borderRadius: "10px", background: theme === "dark" ? "rgba(255,255,255,0.06)" : "#f3f4f6", color: theme === "dark" ? "#d1d5db" : "#374151", fontSize: "14px", fontWeight: "600", border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`, display: "flex", alignItems: "center", gap: "8px", minHeight: "42px" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.5, flexShrink: 0 }}><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                    {holidayForm.clientName || (isRTL ? "لا يوجد عميل" : "No client")}
                  </div>
                </div>

                {/* Working Company */}
                <div>
                  <label style={{ fontSize: "12px", fontWeight: "700", color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#374151", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "6px" }}>
                    {t.workingCompanyLabel} *
                  </label>
                  <ReusableDropdown
                    items={holidayCompanies.map((c) => {
                      const name = typeof c === "string" ? c : (c.companyName || c.name || c.workingCompany || "");
                      return { id: name, name };
                    })}
                    value={holidayForm.workingCompany || ""}
                    onChange={(selectedId) => handleHolidayFormChange("workingCompany", selectedId)}
                    placeholder={t.selectCompany}
                    emptyMessage={t.noCompaniesFound}
                    disabled={!holidayForm.clientName || editingHoliday}
                    theme={theme}
                    language={language}
                    inputStyle={{
                      width: "100%",
                      padding: "11px 14px",
                      borderRadius: "10px",
                      border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.15)" : "#d1d5db"}`,
                      background: editingHoliday ? (theme === "dark" ? "rgba(255,255,255,0.02)" : "#f3f4f6") : (theme === "dark" ? "rgba(255,255,255,0.06)" : "#fff"),
                      color: theme === "dark" ? "#fff" : "#111827",
                      fontSize: "14px",
                      fontWeight: "600",
                      outline: "none",
                      cursor: editingHoliday ? "not-allowed" : (holidayForm.clientName ? "pointer" : "not-allowed"),
                      opacity: editingHoliday ? 0.6 : (holidayForm.clientName ? 1 : 0.5),
                      transition: "all 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    showAllOnArrowClick={true}
                    showClear={false}
                  />
                </div>

                {/* Holiday Name */}
                <div>
                  <label style={{ fontSize: "12px", fontWeight: "700", color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#374151", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "6px" }}>
                    {t.holidayName} *
                  </label>
                  <input
                    type="text"
                    value={holidayForm.name}
                    onChange={(e) => handleHolidayFormChange("name", e.target.value)}
                    placeholder={t.holidayNamePlaceholder}
                    disabled={editingHoliday}
                    style={{ width: "100%", padding: "11px 14px", borderRadius: "10px", border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.15)" : "#d1d5db"}`, background: editingHoliday ? (theme === "dark" ? "rgba(255,255,255,0.02)" : "#f3f4f6") : (theme === "dark" ? "rgba(255,255,255,0.06)" : "#fff"), color: theme === "dark" ? "#fff" : "#111827", fontSize: "14px", outline: "none", boxSizing: "border-box", opacity: editingHoliday ? 0.6 : 1, cursor: editingHoliday ? "not-allowed" : "text" }}
                  />
                </div>

                {/* Date */}
                <div>
                  <label style={{ fontSize: "12px", fontWeight: "700", color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#374151", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "6px" }}>
                    {t.holidayDate} *
                  </label>
                  <input
                    type="date"
                    value={holidayForm.date}
                    onChange={(e) => handleHolidayFormChange("date", e.target.value)}
                    style={{ width: "100%", padding: "11px 14px", borderRadius: "10px", border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.15)" : "#d1d5db"}`, background: theme === "dark" ? "rgba(255,255,255,0.06)" : "#fff", color: theme === "dark" ? "#fff" : "#111827", fontSize: "14px", outline: "none", boxSizing: "border-box" }}
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div style={{ display: "flex", gap: "12px", marginTop: "20px", justifyContent: "flex-end" }}>
                {editingHoliday && (
                  <button
                    onClick={handleCancelEditHoliday}
                    style={{ padding: "10px 22px", borderRadius: "10px", border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.15)" : "#d1d5db"}`, background: "transparent", color: theme === "dark" ? "#fff" : "#374151", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}
                  >
                    {t.cancel}
                  </button>
                )}
                <button
                  onClick={handleSubmitHoliday}
                  disabled={holidaySubmitting}
                  style={{ padding: "10px 28px", borderRadius: "10px", border: "none", background: holidaySubmitting ? "#6b7280" : "linear-gradient(135deg,#059669,#047857)", color: "#fff", fontSize: "14px", fontWeight: "700", cursor: holidaySubmitting ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 4px 14px rgba(5,150,105,0.3)" }}
                >
                  {holidaySubmitting ? (
                    <><Loader size={16} style={{ animation: "spin 1s linear infinite" }} /> {t.loading}</>
                  ) : editingHoliday ? t.saveChanges : t.createHoliday}
                </button>
              </div>
            </div>

            {/* ── Filters Row ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "20px" }}>
              <div style={{ position: "relative" }}>
                <Search size={14} style={{ position: "absolute", top: "50%", [isRTL ? "right" : "left"]: "12px", transform: "translateY(-50%)", color: "#9ca3af", pointerEvents: "none" }} />
                <input
                  type="text"
                  value={holidaySearch}
                  onChange={(e) => setHolidaySearch(e.target.value)}
                  placeholder={isRTL ? "بحث..." : "Search..."}
                  style={{ width: "100%", padding: `9px 12px 9px ${isRTL ? "12px" : "34px"}`, paddingRight: isRTL ? "34px" : "12px", borderRadius: "10px", border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.12)" : "#e5e7eb"}`, background: theme === "dark" ? "rgba(255,255,255,0.05)" : "#f9fafb", color: theme === "dark" ? "#fff" : "#111827", fontSize: "13px", outline: "none", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <ReusableDropdown
                  items={[
                    { id: '', name: t.allClients },
                    // Prefer explicit client list from API, otherwise derive unique clients from the table below
                    ...(holidayClientsList && holidayClientsList.length
                      ? holidayClientsList.map((c) => {
                        const name = typeof c === 'string' ? c : (c.clientName || c.name || c);
                        return { id: name, name };
                      })
                      : [...new Set(holidayList.map((h) => (h.clientName || '').toString().trim()).filter(Boolean))].map((name) => ({ id: name, name }))
                    )
                  ]}
                  value={holidayClientFilter || ''}
                  onChange={(id) => setHolidayClientFilter(id)}
                  placeholder={t.selectClient}
                  emptyMessage={t.noCompaniesFound}
                  maxItems={100}
                  getItemLabel={(item) => item.name || ''}
                  theme={theme}
                  language={language}
                  inputStyle={{ padding: '9px 12px', borderRadius: '10px', border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.12)' : '#e5e7eb'}`, background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb', color: theme === 'dark' ? '#fff' : '#111827', fontSize: '13px', outline: 'none', cursor: 'pointer' }}
                  showAllOnArrowClick={true}
                  showClear={true}
                />
              </div>
              <div>
                <ReusableDropdown
                  items={[{ id: '', name: t.allCompanies }, ...((holidayCompanies && holidayCompanies.length) ? holidayCompanies.map((c) => {
                    const name = typeof c === 'string' ? c : (c.companyName || c.name || c.workingCompany || '');
                    return { id: name, name };
                  }) : [...new Set(holidayList.map((h) => h.workingCompany).filter(Boolean))].map((co) => ({ id: co, name: co })))]}
                  value={holidayCompanyFilter || ''}
                  onChange={(id) => setHolidayCompanyFilter(id)}
                  placeholder={t.allCompanies}
                  emptyMessage={t.noCompaniesFound}
                  maxItems={100}
                  getItemLabel={(item) => item.name || ''}
                  theme={theme}
                  language={language}
                  inputStyle={{ padding: '9px 12px', borderRadius: '10px', border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.12)' : '#e5e7eb'}`, background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb', color: theme === 'dark' ? '#fff' : '#111827', fontSize: '13px', outline: 'none', cursor: 'pointer' }}
                  showAllOnArrowClick={true}
                  showClear={true}
                />
              </div>
            </div>

            {/* ── Table ── */}
            {holidayLoading ? (
              <div style={{ textAlign: "center", padding: "40px", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af" }}>
                <Loader size={28} style={{ animation: "spin 1s linear infinite", marginBottom: "10px" }} />
                <div>{t.loading}</div>
              </div>
            ) : holidayList.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 24px", color: theme === "dark" ? "rgba(255,255,255,0.4)" : "#9ca3af" }}>
                <Calendar size={40} style={{ marginBottom: "12px", opacity: 0.4 }} />
                <div style={{ fontSize: "15px", fontWeight: "600" }}>{t.noHolidaysFound}</div>
              </div>
            ) : (
              <div style={{ overflowX: "auto", borderRadius: "14px", border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.08)" : "#e5e7eb"}` }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ background: theme === "dark" ? "rgba(255,255,255,0.06)" : "#f9fafb" }}>
                      {[t.clientNameLabel, t.workingCompanyLabel, t.holidayName, t.date, t.actions].map((col, i) => (
                        <th key={i} style={{ padding: "12px 16px", textAlign: isRTL ? "right" : "left", fontWeight: "700", color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#374151", whiteSpace: "nowrap", borderBottom: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.08)" : "#e5e7eb"}` }}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {holidayList.map((holiday, idx) => (
                      <tr
                        key={holiday.id}
                        style={{
                          background: idx % 2 === 0 ? "transparent" : (theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"),
                          borderBottom: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.05)" : "#f3f4f6"}`,
                        }}
                      >
                        <td style={{ padding: "13px 16px", color: theme === "dark" ? "#e5e7eb" : "#111827", fontWeight: "500" }}>{holiday.clientName || "—"}</td>
                        <td style={{ padding: "13px 16px", color: theme === "dark" ? "#e5e7eb" : "#374151" }}>{holiday.workingCompany || "—"}</td>
                        <td style={{ padding: "13px 16px", color: theme === "dark" ? "#e5e7eb" : "#374151" }}>{holiday.name}</td>
                        <td style={{ padding: "13px 16px", color: theme === "dark" ? "#e5e7eb" : "#374151", whiteSpace: "nowrap" }}>
                          {holiday.date ? new Date(holiday.date).toLocaleDateString(language === "ar" ? "ar-EG" : "en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                        </td>
                        <td style={{ padding: "13px 16px" }}>
                          {holidayDeleteConfirmId === holiday.id ? (
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                              <span style={{ fontSize: "12px", color: "#ef4444", fontWeight: "600" }}>{t.confirmDeleteHoliday}</span>
                              <button
                                onClick={() => handleDeleteHoliday(holiday.id)}
                                style={{ padding: "5px 12px", borderRadius: "8px", border: "none", background: "#ef4444", color: "#fff", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}
                              >
                                {isRTL ? "نعم" : "Yes"}
                              </button>
                              <button
                                onClick={() => setHolidayDeleteConfirmId(null)}
                                style={{ padding: "5px 12px", borderRadius: "8px", border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.15)" : "#d1d5db"}`, background: "transparent", color: theme === "dark" ? "#fff" : "#374151", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}
                              >
                                {t.cancel}
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: "flex", gap: "8px" }}>
                              <button
                                onClick={() => handleStartEditHoliday(holiday)}
                                title={t.editHoliday}
                                style={{ padding: "6px 10px", borderRadius: "8px", border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.12)" : "#d1d5db"}`, background: theme === "dark" ? "rgba(255,255,255,0.06)" : "#fff", color: "#2e7cba", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: "600" }}
                              >
                                <Settings size={13} /> {t.editHoliday}
                              </button>
                              <button
                                onClick={() => setHolidayDeleteConfirmId(holiday.id)}
                                title={t.deleteHoliday}
                                style={{ padding: "6px 10px", borderRadius: "8px", border: "1px solid rgba(239,68,68,0.3)", background: theme === "dark" ? "rgba(239,68,68,0.1)" : "rgba(239,68,68,0.06)", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: "600" }}
                              >
                                <Trash2 size={13} /> {t.deleteHoliday}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveApprovalPage;
